import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import qrcode from "qrcode-generator";
import { apiRequest, setCsrfToken } from "../api/client.js";
import MemberHistory from "./MemberHistory.jsx";
import Logo from "../shared/Logo.jsx";
import { WORKOUT_PRESETS } from "../shared/workouts.js";
import MockTestTools from "./MockTestTools.jsx";
import WorkoutPlayer from "./WorkoutPlayer.jsx";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Award,
  BarChart3,
  Calendar,
  Check,
  CheckCircle,
  ChevronRight,
  FileText,
  Home,
  Lock,
  LogOut,
  MapPin,
  Plus,
  QrCode,
  RotateCcw,
  Share2,
  Shield,
  User,
  UserPlus,
  X
} from "../shared/icons.jsx";
import { Avatar, BottomNav, Button, Chip, Field, LinkButton, MonoLabel, ProgressBar, SectionHeader, StatBlock, initialsOf } from "../shared/ui.jsx";
import { EVENT_TYPE_LABELS, OFFICIAL_TEST_NOTES, OFFICIAL_TEST_TITLES, dateKey, dayLabel, durationLabel, formatDateTime, militaryTime, toDate } from "../shared/dates.js";
import { WX, HeatAdvisoryChip, useForecast, pickForecast } from "../shared/weather.jsx";
import EventDetail from "./EventDetail.jsx";

const appBarSelectClass =
  "w-full max-w-xs bg-ink border border-ink-soft text-white px-3 py-2 text-sm focus:outline-none focus:border-signal";

const canRole = (principal, role) =>
  principal?.kind === "staff" &&
  principal.roles.some((entry) =>
    entry.role === "platform_admin" || entry.role === role
  );

const canAdminFlight = (principal, flight) =>
  principal?.kind === "staff" && principal.roles.some((role) =>
    role.role === "platform_admin" ||
    (role.role === "squadron_admin" && role.squadron_id === flight.squadron_id) ||
    (role.role === "flight_admin" && role.flight_id === flight.id)
  );

function Notice({ error, clear }) {
  if (!error) return null;
  return (
    <div className="fixed top-4 right-4 z-50 max-w-md bg-red-950 border border-red-500/60 text-red-100 px-4 py-3 flex gap-3 shadow-xl">
      <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
      <div className="text-sm flex-1">{error}</div>
      <button onClick={clear} aria-label="Dismiss"><X size={16} /></button>
    </div>
  );
}

function LoginScreen({ refresh, bootstrapRequired }) {
  const [tab, setTab] = useState(bootstrapRequired ? "bootstrap" : "viewer");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewer, setViewer] = useState({ squadron: "", flight: "", password: "" });
  const [staff, setStaff] = useState({ email: "", password: "" });
  const [member, setMember] = useState({ email: "", password: "" });
  const [request, setRequest] = useState({
    squadronName: "",
    abbreviation: "",
    requesterName: "",
    requesterEmail: "",
    firstFlightName: "",
    password: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [bootstrap, setBootstrap] = useState({
    name: "Alexander Lawrence",
    email: "",
    password: "",
    token: ""
  });

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (tab === "viewer") await apiRequest("viewer_login", { method: "POST", body: viewer });
      // Staff and members both sign in with email + password — staff_login resolves
      // either (it falls back to member accounts). ponytail: one auth path, no
      // squadron/flight pickers for members.
      if (tab === "staff") await apiRequest("staff_login", { method: "POST", body: staff });
      if (tab === "member") await apiRequest("staff_login", { method: "POST", body: member });
      if (tab === "bootstrap") await apiRequest("bootstrap", { method: "POST", body: bootstrap });
      if (tab === "request") {
        const res = await apiRequest("request_squadron", { method: "POST", body: request });
        setSubmitted(res.accessRequest ? "access" : true);
        return;
      }
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const switchTab = (value) => {
    setTab(value);
    setSubmitted(false);
    setError("");
  };

  const segments = [
    ["viewer", "View", "Read-only"],
    ["member", "Member", "Register & track"],
    ["staff", "Staff", "Lead & admin"],
    ...(bootstrapRequired ? [["bootstrap", "Setup", "First run"]] : [])
  ];

  const caption = {
    viewer: "Squadron + password for the read-only calendar. Add a flight for registration access.",
    member: "Sign in with the email and password from your account to register and track your record.",
    staff: "Named account required for leaders and administrators.",
    bootstrap: "Create the first platform administrator. The bootstrap token comes from the server configuration.",
    request: "Platform administration manually reviews every squadron request."
  }[tab];

  return (
    <div className="min-h-screen bg-ink text-white flex flex-col" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <Notice error={error} clear={() => setError("")} />
      <div className="w-full max-w-md mx-auto flex-1 flex flex-col">
        <div className="px-7 pt-12">
          <div className="flex items-center gap-3.5 mb-7">
            <Logo size={54} />
            <div>
              <div className="display text-3xl leading-[0.9] tracking-[0.02em]">PT TRACKER</div>
              <div className="mono text-[9px] uppercase tracking-[0.2em] text-stone-500 mt-0.5">Dispatch · Multi-unit</div>
            </div>
          </div>
          <h1 className="display text-[54px] leading-[0.86] tracking-[0.01em] mb-2.5">
            {tab === "request" ? "Request access." : "Report in."}
          </h1>
          <p className="text-sm leading-normal text-muted min-h-[42px] m-0">{caption}</p>
        </div>

        <form onSubmit={submit} className="flex-1 mt-6 bg-[#0f0e0d] border-t border-[#2a2a2f] px-6 pt-6 pb-7 flex flex-col">
          {tab !== "request" && (
            <div className="flex bg-ink p-1 gap-1 mb-6">
              {segments.map(([value, label, sub]) => {
                const on = tab === value;
                return (
                  <button
                    type="button"
                    key={value}
                    onClick={() => switchTab(value)}
                    className={`flex-1 py-2.5 border-0 cursor-pointer flex flex-col items-center gap-0.5 ${on ? "bg-signal" : "bg-transparent"}`}
                  >
                    <span className={`mono text-[11px] uppercase tracking-[0.14em] ${on ? "text-ink font-semibold" : "text-muted font-medium"}`}>{label}</span>
                    <span className={`mono text-[8px] uppercase tracking-[0.08em] ${on ? "text-ink/70" : "text-[#6b6e74]"}`}>{sub}</span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex flex-col gap-3">
            {tab === "viewer" && (
              <>
                <Field dark placeholder="Squadron name or abbreviation" value={viewer.squadron} onChange={(e) => setViewer({ ...viewer, squadron: e.target.value })} />
                <Field dark placeholder="Flight — blank for squadron view" value={viewer.flight} onChange={(e) => setViewer({ ...viewer, flight: e.target.value })} />
                <Field dark type="password" placeholder="Viewer password" value={viewer.password} onChange={(e) => setViewer({ ...viewer, password: e.target.value })} />
              </>
            )}
            {tab === "staff" && (
              <>
                <Field dark type="email" autoComplete="username" placeholder="Email" value={staff.email} onChange={(e) => setStaff({ ...staff, email: e.target.value })} />
                <Field dark type="password" autoComplete="current-password" placeholder="Password" value={staff.password} onChange={(e) => setStaff({ ...staff, password: e.target.value })} />
              </>
            )}
            {tab === "member" && (
              <>
                <Field dark type="email" autoComplete="username" placeholder="Email" value={member.email} onChange={(e) => setMember({ ...member, email: e.target.value })} />
                <Field dark type="password" autoComplete="current-password" placeholder="Password" value={member.password} onChange={(e) => setMember({ ...member, password: e.target.value })} />
              </>
            )}
            {tab === "request" && !submitted && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field dark placeholder="Squadron name" value={request.squadronName} onChange={(e) => setRequest({ ...request, squadronName: e.target.value })} />
                  <Field dark placeholder="Abbreviation" value={request.abbreviation} onChange={(e) => setRequest({ ...request, abbreviation: e.target.value })} />
                </div>
                <Field dark placeholder="Requester name" value={request.requesterName} onChange={(e) => setRequest({ ...request, requesterName: e.target.value })} />
                <Field dark type="email" placeholder="Requester email" value={request.requesterEmail} onChange={(e) => setRequest({ ...request, requesterEmail: e.target.value })} />
                <Field dark placeholder="Requested first flight" value={request.firstFlightName} onChange={(e) => setRequest({ ...request, firstFlightName: e.target.value })} />
                <Field dark type="password" minLength={8} placeholder="Initial administrator password" value={request.password} onChange={(e) => setRequest({ ...request, password: e.target.value })} />
              </>
            )}
            {tab === "bootstrap" && (
              <>
                <Field dark placeholder="Administrator name" value={bootstrap.name} onChange={(e) => setBootstrap({ ...bootstrap, name: e.target.value })} />
                <Field dark type="email" placeholder="Administrator email" value={bootstrap.email} onChange={(e) => setBootstrap({ ...bootstrap, email: e.target.value })} />
                <Field dark type="password" minLength={8} placeholder="Administrator password" value={bootstrap.password} onChange={(e) => setBootstrap({ ...bootstrap, password: e.target.value })} />
                <Field dark type="password" placeholder="Bootstrap token" value={bootstrap.token} onChange={(e) => setBootstrap({ ...bootstrap, token: e.target.value })} />
              </>
            )}
          </div>

          {tab === "request" && submitted ? (
            <div className="mt-4 border border-pass bg-[rgba(95,107,57,0.14)] text-pass p-4 text-sm">
              {submitted === "access"
                ? "That organization already exists — your request for access was sent to its administrators for review."
                : "Your request was submitted for manual platform review."}
            </div>
          ) : (
            <Button size="lg" disabled={busy} className="w-full h-[54px] mt-5 tracking-[0.18em]">
              {busy
                ? "Working..."
                : tab === "request"
                  ? "Submit request"
                  : tab === "bootstrap"
                    ? "Create platform admin"
                    : "Sign in"}
              {!busy && <ArrowRight size={16} />}
            </Button>
          )}

          <div className="mt-auto pt-6 flex items-center justify-between">
            {tab === "request" ? (
              <button
                type="button"
                onClick={() => switchTab("viewer")}
                className="mono text-[10px] font-semibold uppercase tracking-[0.12em] text-muted bg-transparent border-0 underline cursor-pointer p-0 flex items-center gap-1.5"
              >
                <ArrowLeft size={12} />Back to sign in
              </button>
            ) : (
              <>
                <span className="mono text-[10px] tracking-[0.08em] text-[#6b6e74]">No squadron yet?</span>
                <button
                  type="button"
                  onClick={() => switchTab("request")}
                  className="mono text-[10px] font-semibold uppercase tracking-[0.12em] text-signal-bright bg-transparent border-0 underline cursor-pointer p-0"
                >
                  Request access
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// Public landing for a scanned barcode. ?invite=<token> → enrol with name/email/
// password (flight + squadron come from the invite); an email that already has an
// account in that flight recovers it instead. ?login=<token> → one-time staff
// sign-in. ?reset=<token> → member sets a new password. The token carries the rest.
function RedeemScreen({ inviteToken, loginToken, resetToken, onSignedIn, onCancel }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [loginState, setLoginState] = useState(loginToken ? "working" : null);

  useEffect(() => {
    if (!loginToken) return;
    let live = true;
    (async () => {
      try {
        const res = await apiRequest("redeem_login_pass", { method: "POST", body: { token: loginToken } });
        setCsrfToken(res.csrfToken);
        if (live) onSignedIn();
      } catch (err) {
        if (live) { setLoginState("error"); setError(err.message); }
      }
    })();
    return () => { live = false; };
  }, [loginToken, onSignedIn]);

  const submit = async (event) => {
    event.preventDefault();
    if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setBusy(true); setError("");
    try {
      const res = resetToken
        ? await apiRequest("redeem_member_reset", { method: "POST", body: { token: resetToken, password: form.password } })
        : await apiRequest("redeem_flight_invite", { method: "POST", body: { token: inviteToken, name: form.name, email: form.email, password: form.password } });
      if (res.loggedIn) {
        if (res.csrfToken) setCsrfToken(res.csrfToken);
        onSignedIn(); // now signed in; an invite enrolment then shows the credential prompt
        return;
      }
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-ink text-white flex flex-col" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <Notice error={error} clear={() => setError("")} />
      <div className="w-full max-w-md mx-auto flex-1 flex flex-col">
        <div className="px-7 pt-12">
          <div className="flex items-center gap-3.5 mb-7">
            <Logo size={54} />
            <div>
              <div className="display text-3xl leading-[0.9] tracking-[0.02em]">PT TRACKER</div>
              <div className="mono text-[9px] uppercase tracking-[0.2em] text-stone-500 mt-0.5">Dispatch · Multi-unit</div>
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  );

  if (loginToken) {
    return shell(
      <>
        <h1 className="display text-[44px] leading-[0.9] mb-2.5">{loginState === "error" ? "Link expired." : "Signing you in…"}</h1>
        <p className="text-sm leading-normal text-muted m-0">
          {loginState === "error" ? "This one-time login barcode is invalid, used, or expired. Ask a platform admin for a new one." : "One moment while we open your account."}
        </p>
        {loginState === "error" && <Button type="button" className="mt-6" onClick={onCancel}>Back to sign in</Button>}
      </>
    );
  }

  if (result) {
    // Reached only when a signed-in staff member enrolled someone else.
    return shell(
      <>
        <h1 className="display text-[44px] leading-[0.9] mb-2.5">{result.recovery ? "Account recovered." : "Member enrolled."}</h1>
        <p className="text-sm leading-normal text-muted m-0">The account is active. They can sign in with their email and password.</p>
        <Button type="button" className="mt-6" onClick={onCancel}>Done</Button>
      </>
    );
  }

  if (resetToken) {
    return shell(
      <>
        <h1 className="display text-[44px] leading-[0.9] mb-2.5">Set a new password.</h1>
        <p className="text-sm leading-normal text-muted min-h-[42px] m-0">Choose a new password for your account. You'll be signed in right after.</p>
        <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
          <Field dark type="password" minLength={8} autoComplete="new-password" placeholder="New password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <Button className="mt-2" disabled={busy || form.password.length < 8}>{busy ? "Saving…" : "Save password"}</Button>
          <button type="button" onClick={onCancel} className="bg-transparent border-0 text-muted mono text-[10px] uppercase tracking-[0.14em] cursor-pointer mt-1">Back to sign in</button>
        </form>
      </>
    );
  }

  return shell(
    <>
      <h1 className="display text-[44px] leading-[0.9] mb-2.5">Join the team.</h1>
      <p className="text-sm leading-normal text-muted min-h-[42px] m-0">Enter your details to register. Your unit is set by the invite.</p>
      <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
        <Field dark placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Field dark type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Field dark type="password" minLength={8} placeholder="Password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <Button className="mt-2" disabled={busy || !form.name.trim() || !form.email.trim()}>{busy ? "Creating…" : "Create account"}</Button>
        <button type="button" onClick={onCancel} className="bg-transparent border-0 text-muted mono text-[10px] uppercase tracking-[0.14em] cursor-pointer mt-1">Already have an account? Sign in</button>
      </form>
    </>
  );
}

// Mandatory prompt shown right after an invite enrolment: the new member confirms
// or changes the email + password they'll sign in with. Cannot be dismissed.
function MemberCredentialModal({ principal, onSaved }) {
  const [form, setForm] = useState({ email: principal.username || "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      await apiRequest("update_member_credentials", { method: "POST", body: { email: form.email, password: form.password } });
      await onSaved();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 p-4 flex items-center justify-center">
      <form className="bg-white p-5 w-full max-w-md space-y-3" onSubmit={submit}>
        <h2 className="display text-3xl text-ink m-0">Confirm your login</h2>
        <p className="mono text-[10px] text-ink bg-signal-tint border border-signal px-3 py-2">
          You're enrolled in {principal.flightName}. Set the email and password you'll use to sign in from now on.
        </p>
        {error && <div className="mono text-[10px] text-fail">{error}</div>}
        <Field type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Field type="password" minLength={8} placeholder="New password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <Button className="w-full" disabled={busy || !form.email.trim() || form.password.length < 8}>{busy ? "Saving…" : "Save and continue"}</Button>
      </form>
    </div>
  );
}

function WeekStrip({ eventDayKeys, selectedDay, onSelectDay }) {
  const ref = useRef(null);
  const drag = useRef(null);
  const moved = useRef(false);
  const today = new Date();
  const todayCode = dateKey(today);
  // 3 weeks back to 4 weeks forward, scrollable
  const start = new Date(today);
  start.setDate(today.getDate() - 21);
  const days = Array.from({ length: 56 }, (_, i) => {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    return date;
  });

  // center on today on mount
  useEffect(() => {
    ref.current?.querySelector(`[data-key="${todayCode}"]`)?.scrollIntoView({ inline: "center", block: "nearest" });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // mouse drag-to-scroll; touch uses native overflow scroll
  const down = (e) => {
    if (e.pointerType !== "mouse") return;
    drag.current = { x: e.clientX, left: ref.current.scrollLeft };
    moved.current = false;
  };
  const move = (e) => {
    if (e.pointerType !== "mouse" || !drag.current) return;
    const dx = e.clientX - drag.current.x;
    if (Math.abs(dx) > 4) moved.current = true;
    ref.current.scrollLeft = drag.current.left - dx;
  };
  const up = () => { drag.current = null; };

  return (
    <div
      ref={ref}
      className="mt-3 flex gap-1.5 overflow-x-auto select-none cursor-grab active:cursor-grabbing [&::-webkit-scrollbar]:hidden"
      style={{ scrollbarWidth: "none" }}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerLeave={up}
    >
      {days.map((date) => {
        const key = dateKey(date);
        const isToday = key === todayCode;
        const isSelected = key === selectedDay;
        const hasEvent = eventDayKeys.has(key);
        return (
          <button
            key={key}
            data-key={key}
            onClick={() => { if (moved.current) { moved.current = false; return; } onSelectDay(key); }}
            className={`flex-shrink-0 w-12 text-center pt-2 pb-1.5 rounded-[3px] border-0 cursor-pointer ${isSelected ? "bg-ink" : isToday ? "bg-signal" : "bg-transparent"}`}
          >
            <div className={`mono text-[9px] uppercase tracking-[0.1em] ${isSelected ? "text-surface/60" : isToday ? "text-ink/60" : "text-[#6b6e74]"}`}>
              {date.toLocaleDateString(undefined, { weekday: "short" })}
            </div>
            <div className={`display text-[22px] leading-none mt-0.5 ${isSelected ? "text-surface" : isToday ? "text-ink" : "text-[#e7e7ea]"}`}>
              {String(date.getDate()).padStart(2, "0")}
            </div>
            <div
              className="w-[5px] h-[5px] rounded-full mx-auto mt-1"
              style={{ background: isSelected ? "#fff" : isToday ? "#1b1b1f" : hasEvent ? "oklch(0.62 0.16 47)" : "transparent" }}
            />
          </button>
        );
      })}
    </div>
  );
}

// Color scheme to delineate session types at a glance: a left accent bar on the
// card + a tinted label pill. PT (Conditioning) = sky, Mock test (Assessment) =
// violet, Official test = amber with a striped card so it can't be mistaken for a
// practice run. ponytail: static map + CSS gradient, no theming system or image asset.
const TYPE_ACCENT = {
  PT: { bar: "border-sky-500", pill: "bg-sky-100 text-sky-800" },
  MOCK_TEST: { bar: "border-violet-500", pill: "bg-violet-100 text-violet-800" },
  OFFICIAL_TEST: {
    bar: "border-amber-500",
    pill: "bg-amber-300 text-amber-900",
    pattern: "repeating-linear-gradient(45deg, #fffbeb, #fffbeb 10px, #fde68a 10px, #fde68a 20px)"
  },
  OTHER: { bar: "border-stone-300", pill: "bg-stone-100 text-stone-700" }
};
const typeAccent = (eventType) => TYPE_ACCENT[eventType] || TYPE_ACCENT.OTHER;

function PastEventCard({ event, attended, onOpen }) {
  return (
    <article className={`bg-[#f3f4f6] opacity-85 flex mb-3 cursor-pointer border-l-4 ${typeAccent(event.eventType).bar}`} style={typeAccent(event.eventType).pattern ? { backgroundImage: typeAccent(event.eventType).pattern } : undefined} onClick={onOpen}>
      <div className="w-16 bg-[#dcdee1] text-[#8a8d93] flex flex-col items-center justify-center py-3 flex-shrink-0">
        <div className="display text-2xl leading-[0.85]">{militaryTime(toDate(event.startsAt))}</div>
        <div className="mono text-[8px] tracking-[0.14em] mt-1">HRS</div>
      </div>
      <div className="flex-1 px-4 py-3 flex items-center justify-between gap-3 min-w-0">
        <div className="min-w-0">
          <h3 className="display text-2xl leading-[0.92] text-[#6b6e74] m-0 truncate">{event.title}</h3>
          <div className="mono text-[10px] text-muted mt-1 truncate">
            {event.location || "No location"} · {event.status === "canceled" ? "Canceled" : `${event.attendanceCount} registered`}
          </div>
        </div>
        {attended && <CheckCircle size={20} className="text-pass flex-shrink-0" />}
      </div>
    </article>
  );
}

// Compact forecast for a calendar row: hour temp within 72h, else day hi/lo. Shows
// precip only when notable (≥30%). Renders nothing when out of range or no location set.
function WeatherChip({ forecast, startsAt }) {
  const wx = pickForecast(forecast, startsAt);
  if (!wx) return null;
  const [label, icon] = WX[wx.code] || ["", "🌡️"];
  return (
    <div className="flex flex-col items-end gap-1 mt-1">
      <span className="mono text-[9px] text-stone-600 flex items-center gap-1 whitespace-nowrap" title={label}>
        <span className="text-[11px] leading-none">{icon}</span>
        {wx.kind === "hour" ? `${wx.temp}°` : `${wx.hi}°/${wx.lo}°`}
        {wx.precip != null && wx.precip >= 30 ? ` · ${wx.precip}%` : ""}
      </span>
      <HeatAdvisoryChip wx={wx} />
    </div>
  );
}

function EventCard({ event, principal, ownedRegistrations, forecast, tone = "ink", onOpen, onRegister, onRemove }) {
  const [name, setName] = useState("");
  const isStaff = principal.kind === "staff";
  const canAnonymousRegister = principal.kind === "viewer" && principal.scope === "flight" && event.status === "scheduled" && !event.registrationLocked;
  // Staff register themselves with their own account name (one tap, no typing).
  const myStaffReg = isStaff ? ((ownedRegistrations[event.id] || []).find((r) => r.participantName === principal.name) || null) : null;
  const canMemberRegister = principal.kind === "member" && event.status === "scheduled" && !event.registrationLocked && !event.memberRegistrationId;
  const owned = ownedRegistrations[event.id] || [];

  const canceled = event.status === "canceled";
  // Past its run time + 15 min (registration_closed from the API): gray it out
  // like a finished event. Canceled keeps its own red treatment.
  const locked = event.registrationLocked && !canceled;
  const blockClass = canceled || locked || tone === "past"
    ? "bg-[#c9ccd0] text-[#5a5d63]"
    : tone === "signal"
      ? "bg-signal text-ink"
      : "bg-ink text-white";
  const accent = typeAccent(event.eventType);

  return (
    <article className={`bg-white mb-3 border-l-4 ${accent.bar} ${tone === "past" || locked ? "opacity-90" : ""}`} style={accent.pattern ? { backgroundImage: accent.pattern } : undefined}>
      <div className="cursor-pointer" onClick={onOpen}>
        <div className="flex">
          <div className={`w-16 flex flex-col items-center justify-center py-3.5 flex-shrink-0 ${blockClass}`}>
            {durationLabel(event.durationMinutes) ? (
              <>
                <div className="mono text-[9px] tracking-[0.16em] font-semibold">{militaryTime(toDate(event.startsAt))}</div>
                <div className="display text-[30px] leading-[0.82] mt-0.5">{durationLabel(event.durationMinutes)}</div>
                <div className="mono text-[8px] tracking-[0.14em] mt-0.5">BLOCK</div>
              </>
            ) : (
              <>
                <div className="display text-[28px] leading-[0.85]">{militaryTime(toDate(event.startsAt))}</div>
                <div className="mono text-[8px] tracking-[0.14em] mt-1">HRS</div>
              </>
            )}
          </div>
          <div className="flex-1 px-4 py-3 min-w-0">
            <div className="flex justify-between items-start gap-2">
              {canceled || locked ? (
                <span className={`mono text-[9px] uppercase tracking-[0.14em] ${canceled ? "text-red-700" : "text-muted"}`}>
                  {canceled ? "● Canceled" : "● Closed"}
                </span>
              ) : (
                <span className={`mono text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 ${accent.pill}`}>
                  {EVENT_TYPE_LABELS[event.eventType] || "Event"}
                </span>
              )}
              <div className="flex flex-col items-end flex-shrink-0">
                <span className="mono text-[9px] uppercase tracking-[0.1em] text-muted truncate">{event.ownerFlightName}</span>
                <WeatherChip forecast={forecast} startsAt={event.startsAt} />
              </div>
            </div>
            <h3 className="display text-3xl leading-[0.92] text-ink mt-1 mb-1.5">{event.title}</h3>
            <div className="mono text-[11px] text-stone-600 flex items-center gap-2">
              <MapPin size={13} className="text-muted flex-shrink-0" />
              {event.location || "Location pending"}
            </div>
            {event.leadName && (
              <div className="mono text-[11px] text-stone-600 flex items-center gap-2 mt-1">
                <User size={13} className="text-muted flex-shrink-0" />
                {event.leadName}
              </div>
            )}
          </div>
        </div>
        <div className={`px-4 pt-3 ${isStaff ? "pb-4" : "pb-1"}`}>
          <div className="flex justify-between mono text-[9px] uppercase tracking-[0.1em] text-muted">
            <span>Turnout</span>
            <span className="text-ink">
              {event.capacity > 0
                ? `${event.attendanceCount} / ${event.capacity}${event.attendanceCount >= event.capacity ? " · Full" : ""}`
                : `${event.attendanceCount} registered`}
            </span>
          </div>
          {event.capacity > 0 && (
            <ProgressBar
              value={event.attendanceCount}
              max={event.capacity}
              full={event.attendanceCount >= event.capacity}
              className="mt-1.5"
            />
          )}
        </div>
      </div>

      {principal.kind === "member" && !canceled && (
        <div className="px-4 pb-4 pt-2">
          {event.memberRegistrationId ? (
            <div className="flex items-center justify-between h-12 px-3.5 border-[1.5px] border-signal">
              <span className="flex items-center gap-2 mono text-[11px] font-semibold uppercase tracking-[0.1em] text-signal-deep">
                <Check size={15} />Registered
              </span>
              <LinkButton onClick={() => onRemove({ registrationId: event.memberRegistrationId })}>Remove</LinkButton>
            </div>
          ) : canMemberRegister ? (
            <Button className="w-full" onClick={() => onRegister(event.id)}>Register</Button>
          ) : null}
        </div>
      )}

      {isStaff && !canceled && event.status === "scheduled" && (
        <div className="px-4 pb-4 pt-2">
          {myStaffReg ? (
            <div className="flex items-center justify-between h-12 px-3.5 border-[1.5px] border-signal">
              <span className="flex items-center gap-2 mono text-[11px] font-semibold uppercase tracking-[0.1em] text-signal-deep">
                <Check size={15} />Registered
              </span>
              <LinkButton onClick={() => onRemove(myStaffReg)}>Remove</LinkButton>
            </div>
          ) : event.registrationLocked ? (
            <div className="h-12 flex items-center justify-center bg-line-soft text-muted mono text-[11px] font-semibold uppercase tracking-[0.14em]">Registration closed</div>
          ) : (
            <Button className="w-full" onClick={() => onRegister(event.id, principal.name, true)}>Register</Button>
          )}
        </div>
      )}

      {canAnonymousRegister && (
        <div className="px-4 pb-4 pt-2">
          <div className="flex gap-2">
            <Field className="h-12 flex-1" placeholder="Rank Last, First" value={name} onChange={(e) => setName(e.target.value)} />
            <Button disabled={!name.trim()} onClick={() => { onRegister(event.id, name); setName(""); }}>
              Register
            </Button>
          </div>
          {owned.length > 0 && (
            <div className="mt-3">
              <MonoLabel className="text-[9px]">Added on this device</MonoLabel>
              {owned.map((registration) => (
                <div key={registration.registrationId} className="flex justify-between items-center mt-2 text-sm text-ink">
                  <span>{registration.participantName}</span>
                  <LinkButton onClick={() => onRemove(registration)}>Remove</LinkButton>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {canceled && !isStaff && (
        <div className="mx-4 mb-4 mt-2 h-12 flex items-center justify-center bg-line-soft text-muted mono text-[11px] font-semibold uppercase tracking-[0.14em]">
          Registration closed
        </div>
      )}
    </article>
  );
}

// Create-only. Editing an existing event is done inline in EventDetail; this modal
// is only ever opened as { mode: "create" }.
function EventModal({ flights, principal, close, save }) {
  const availableFlights = flights.filter((flight) =>
    principal.kind === "staff" &&
    (principal.roles.some((role) =>
      role.role === "platform_admin" ||
      (role.role === "squadron_admin" && role.squadron_id === flight.squadron_id)
    ) ||
      principal.roles.some((role) => role.flight_id === flight.id))
  );
  const [form, setForm] = useState({
    ownerFlightId: availableFlights[0]?.id || "",
    title: "",
    startsAt: "",
    durationMinutes: "",
    capacity: "",
    leadName: "",
    location: "",
    eventType: "PT",
    notes: "",
    workoutId: "",
    status: "scheduled",
    repeat: "none",
    occurrences: 8
  });

  const normalized = () => ({
    ...form,
    durationMinutes: form.durationMinutes === "" ? null : Number(form.durationMinutes),
    capacity: form.capacity === "" ? null : Number(form.capacity)
  });

  return (
    <div className="fixed inset-0 z-40 bg-black/60 p-4 flex items-center justify-center" onClick={close}>
      <form className="bg-white p-5 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto" onSubmit={(e) => { e.preventDefault(); save(normalized()); }} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h2 className="display text-3xl text-ink m-0">Create Event</h2>
          <button type="button" onClick={close} className="text-stone-600 bg-transparent border-0 cursor-pointer p-1" aria-label="Close"><X size={18} /></button>
        </div>
        <Field as="select" value={form.ownerFlightId} onChange={(e) => setForm({ ...form, ownerFlightId: e.target.value })}>
          {availableFlights.map((flight) => <option key={flight.id} value={flight.id}>{flight.name}</option>)}
        </Field>
        <Field
          as="select"
          value=""
          onChange={(e) => {
            const preset = WORKOUT_PRESETS.find((w) => w.id === e.target.value);
            if (preset) setForm({
              ...form,
              title: preset.title,
              eventType: preset.eventType,
              durationMinutes: String(preset.durationMinutes),
              notes: preset.notes,
              // Attach the guided player only for presets that have segments.
              workoutId: preset.segments?.length ? preset.id : ""
            });
          }}
        >
          <option value="">Load a workout… (optional)</option>
          {WORKOUT_PRESETS.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
        </Field>
        {form.eventType === "OFFICIAL_TEST" ? (
          <Field as="select" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}>
            {OFFICIAL_TEST_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
          </Field>
        ) : (
          <Field placeholder="Event title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        )}
        <Field type="datetime-local" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2 mono text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.repeat === "weekly"}
              onChange={(e) => setForm({ ...form, repeat: e.target.checked ? "weekly" : "none" })}
            />
            Repeats weekly
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field type="number" min="5" max="1440" placeholder="Duration (minutes)" value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })} />
          <Field type="number" min="1" max="1000" placeholder="Capacity (optional)" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
        </div>
        <Field placeholder="Session lead (optional)" value={form.leadName} onChange={(e) => setForm({ ...form, leadName: e.target.value })} />
        <Field placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        <Field
          as="select"
          value={form.eventType}
          onChange={(e) => {
            const eventType = e.target.value;
            // Picking Official test defaults the title to a valid option and
            // stamps the mandatory prep reminder into notes so nobody forgets it.
            setForm(eventType === "OFFICIAL_TEST"
              ? {
                  ...form,
                  eventType,
                  title: OFFICIAL_TEST_TITLES.includes(form.title) ? form.title : OFFICIAL_TEST_TITLES[0],
                  notes: form.notes || OFFICIAL_TEST_NOTES
                }
              : { ...form, eventType });
          }}
        >
          <option value="PT">Physical Training</option>
          <option value="MOCK_TEST">Mock PT Test</option>
          <option value="OFFICIAL_TEST">Official PT Test</option>
          <option value="OTHER">Other</option>
        </Field>
        <Field as="textarea" rows={4} placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <Button className="w-full">Save Event</Button>
      </form>
    </div>
  );
}

function ShareModal({ event, flights, close, share }) {
  const eligibleFlights = flights.filter((flight) =>
    flight.id !== event.ownerFlightId &&
    (!flight.squadron_id || flight.squadron_id === event.squadronId)
  );
  const [flightId, setFlightId] = useState(
    eligibleFlights[0]?.id || ""
  );
  return (
    <div className="fixed inset-0 z-40 bg-black/60 p-4 flex items-center justify-center" onClick={close}>
      <form className="bg-white p-5 w-full max-w-md space-y-3" onSubmit={(e) => { e.preventDefault(); share(event.id, flightId); }} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h2 className="display text-3xl text-ink m-0">Share Event</h2>
          <button type="button" onClick={close} className="text-stone-600 bg-transparent border-0 cursor-pointer p-1" aria-label="Close"><X size={18} /></button>
        </div>
        <p className="text-sm text-stone-600">The recipient flight admin must approve this event before it appears on their calendar.</p>
        <Field as="select" value={flightId} onChange={(e) => setFlightId(e.target.value)}>
          {eligibleFlights.map((flight) => <option key={flight.id} value={flight.id}>{flight.name}</option>)}
        </Field>
        <Button className="w-full" disabled={!flightId}>Send for Approval</Button>
      </form>
    </div>
  );
}

function MemberAccountRequest({ error }) {
  const [form, setForm] = useState({
    displayName: "",
    rank: "",
    username: "",
    password: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    try {
      await apiRequest("request_member_account", { method: "POST", body: form });
      setSubmitted(true);
    } catch (err) {
      error(err.message);
    }
  };
  return (
    <div className="max-w-xl">
      <div className="bg-ink -mx-4 -mt-5 px-5 pt-4 pb-4 mb-5">
        <h2 className="display text-[34px] leading-[0.9] text-white m-0">MEMBER ACCOUNT</h2>
        <div className="mono text-[9px] uppercase tracking-[0.16em] text-stone-500 mt-1">Optional · Flight-admin approval</div>
      </div>
      <p className="text-sm text-stone-600 mb-5 mt-0">
        Accounts are optional and require flight-admin approval. Do not enter DoD ID, birth date, phone, or other personal identifiers.
      </p>
      {submitted ? (
        <div className="bg-[rgba(95,107,57,0.14)] text-pass-deep p-4 mono text-xs font-semibold uppercase tracking-[0.1em]">
          Your request is pending flight-admin approval.
        </div>
      ) : (
        <form className="bg-white p-4 space-y-3" onSubmit={submit}>
          <Field placeholder="Display name" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
          <Field placeholder="Rank (optional)" value={form.rank} onChange={(e) => setForm({ ...form, rank: e.target.value })} />
          <Field placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
          <Field type="password" minLength={8} placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <Button className="w-full">Request Account</Button>
        </form>
      )}
    </div>
  );
}

const ROLE_LABELS = {
  platform_admin: "Platform Admin",
  squadron_admin: "Squadron Admin",
  flight_admin: "Flight Admin",
  leader: "Leader"
};

// Highest wins when a user holds several roles, and orders the staff list (top first):
// platform > squadron > flight > leader > member.
const ROLE_RANK = { platform_admin: 5, squadron_admin: 4, flight_admin: 3, leader: 2, member: 1 };

const affiliationLabel = (a) =>
  (ROLE_LABELS[a.role] || a.role) +
  (a.squadron_name ? ` · ${a.squadron_name}` : "") +
  (a.flight_name ? ` · ${a.flight_name}` : "");

// Self: edit own name/email/password. Admin: edit another user's basics and
// (platform admin only) associate them with a squadron/flight.
// URL a phone scan should open. The redeem screen reads ?invite= / ?login=.
const scanUrl = (key, token) =>
  `${window.location.origin}${window.location.pathname}?${key}=${token}`;

// Render text as a scannable QR (client-side, zero network — the token never
// leaves the device except to our own API). Type 0 auto-sizes; H error level so
// the center stays readable under the overlaid logo (~30% recoverable).
const qrDataUrl = (text) => {
  const qr = qrcode(0, "H");
  qr.addData(text);
  qr.make();
  return qr.createDataURL(6, 2);
};

// Shows a freshly minted access barcode as a scannable QR plus the raw token.
function IssuedBarcode({ label, code, url, hint, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const scan = url || code;
  const img = useMemo(() => qrDataUrl(scan), [scan]);
  return (
    <div className="bg-signal-tint border border-signal p-3 space-y-2">
      <div className="flex items-center justify-between">
        <MonoLabel className="text-[9px] flex items-center gap-1.5"><QrCode size={13} /> {label}</MonoLabel>
        {onDismiss && <button type="button" onClick={onDismiss} aria-label="Dismiss" className="bg-transparent border-0 text-signal-deep cursor-pointer p-0.5 flex"><X size={14} /></button>}
      </div>
      <div className="flex justify-center bg-white border border-line p-3">
        <div className="relative" style={{ width: 188, height: 188 }}>
          <img src={img} alt={`${label} QR code`} width={188} height={188} className="[image-rendering:pixelated] block w-full h-full" />
          {/* Logo over the center — QR is level H, so the covered modules stay recoverable. */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-1">
            <Logo size={38} />
          </div>
        </div>
      </div>
      <div className="mono text-[11px] break-all bg-white border border-line px-2 py-1.5 select-all">{code}</div>
      <div className="flex items-center gap-2">
        <Button size="sm" type="button" variant="outline" onClick={() => { navigator.clipboard?.writeText(scan); setCopied(true); }}>{copied ? "Copied" : url ? "Copy link" : "Copy code"}</Button>
        {hint && <span className="mono text-[9px] text-muted">{hint}</span>}
      </div>
    </div>
  );
}

// Connect a wearable (Google Health) to your own account and pull daily totals.
// Attaches to the member linked to your staff account; feeds the unit stats page.
function AccountFitness() {
  const [state, setState] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = async () => {
    try { setState(await apiRequest("fitness_status")); setFailed(false); }
    catch { setFailed(true); }
  };
  useEffect(() => { load(); }, []);
  if (failed) return (
    <div className="border border-line p-3 mono text-[10px] text-muted">
      Activity sync endpoint missing — deploy the latest <span className="text-ink">api.php</span> to this server.
    </div>
  );
  if (!state) return null; // still loading
  if (!state.configured) return (
    <div className="border border-line p-3 mono text-[10px] text-muted">
      Activity sync isn't configured. Set <span className="text-ink">google_health_client_id</span> and <span className="text-ink">fitness_redirect_uri</span> in <span className="text-ink">api.config.php</span>, then reload.
    </div>
  );
  const conn = (state.connections || []).find((c) => c.provider === "google_health");
  const w = state.summary?.windows || { "7": {}, "30": {}, all: {} };
  const fmt = (n) => Number(n || 0).toLocaleString();
  const connect = async () => {
    setErr("");
    try {
      const res = await apiRequest("connect_fitness", { method: "POST", body: { provider: "google_health" } });
      if (!res.authorizeUrl) { setErr("Server did not return an authorize URL."); return; }
      window.location.href = res.authorizeUrl;
    } catch (e) { setErr(e.message || "Couldn't start the connection."); }
  };
  const sync = async () => {
    setErr(""); setBusy(true);
    try { await apiRequest("sync_fitness", { method: "POST", body: {} }); await load(); }
    catch (e) { setErr(e.message || "Sync failed."); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Health? Synced history is kept.")) return;
    setErr("");
    try { await apiRequest("disconnect_fitness", { method: "POST", body: { provider: "google_health" } }); await load(); }
    catch (e) { setErr(e.message || "Disconnect failed."); }
  };
  const Row = ({ label, field }) => (
    <tr>
      <td className="mono text-[10px] uppercase tracking-[0.1em] text-muted py-0.5">{label}</td>
      <td className="text-right text-ink tabular-nums">{fmt(w["7"]?.[field])}</td>
      <td className="text-right text-ink tabular-nums">{fmt(w["30"]?.[field])}</td>
      <td className="text-right text-ink tabular-nums">{fmt(w.all?.[field])}</td>
    </tr>
  );
  return (
    <div className="border border-line p-3">
      <div className="flex justify-between items-center mb-2">
        <MonoLabel className="text-[9px]">Activity sync</MonoLabel>
        {conn ? (
          <button type="button" onClick={sync} disabled={busy} className="mono text-[10px] font-semibold uppercase tracking-[0.1em] text-signal-deep bg-transparent border border-signal px-2 py-1 cursor-pointer disabled:opacity-50">
            {busy ? "Syncing…" : "Sync"}
          </button>
        ) : (
          <button type="button" onClick={connect} className="mono text-[10px] font-semibold uppercase tracking-[0.1em] text-white bg-ink px-2 py-1 cursor-pointer border-0">
            Connect Google Health
          </button>
        )}
      </div>
      {err && <div className="mono text-[10px] text-fail mb-2">{err}</div>}
      {conn ? (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="mono text-[8px] uppercase tracking-[0.1em] text-muted">
                <th></th><th className="text-right font-normal">7d</th><th className="text-right font-normal">30d</th><th className="text-right font-normal">All</th>
              </tr>
            </thead>
            <tbody>
              <Row label="Steps" field="steps" />
              <Row label="Miles" field="miles" />
              <Row label="Cardio min" field="cardio" />
            </tbody>
          </table>
          <div className="flex justify-between items-center mt-2">
            <span className="mono text-[9px] text-muted">{conn.status === "error" ? "Sync error — reconnect" : conn.last_synced_at ? `Synced ${conn.last_synced_at}` : "Not synced yet"}</span>
            <button type="button" onClick={disconnect} className="mono text-[10px] text-fail bg-transparent border-0 cursor-pointer p-0 underline">Disconnect</button>
          </div>
        </>
      ) : (
        <div className="mono text-[10px] text-muted">Pull your daily steps, miles, and cardio minutes into your stats.</div>
      )}
    </div>
  );
}

function UserModal({ mode, principal, target, affiliations, squadrons, flights, forced, scopeSquadrons, scopeFlights, scopeSquadron, onScopeSquadron, flightScope, onFlightScope, close, saved, error }) {
  const isSelf = mode === "self";
  const base = isSelf ? principal : target;
  const [form, setForm] = useState({ name: base.name || "", rank: base.rank || base.rank_label || "", email: base.email || "", password: "" });
  const [assoc, setAssoc] = useState({ squadronId: "", flightId: "" });
  const isPlatform = principal.roles?.some((r) => r.role === "platform_admin");
  // Login barcodes go to admins and leaders (not plain members), never the issuer.
  const targetIsAdmin = (affiliations || []).some((a) => ["platform_admin", "squadron_admin", "flight_admin", "leader"].includes(a.role));
  const canIssueLoginPass = !isSelf && isPlatform && targetIsAdmin && principal.id !== target.id;
  const [pwReset, setPwReset] = useState("");
  const [note, setNote] = useState("");
  const [loginPass, setLoginPass] = useState(null);

  const resetPassword = async () => {
    if (pwReset.length < 8) { error("New password must be at least 8 characters."); return; }
    try {
      await apiRequest("reset_staff_credentials", { method: "POST", body: { userId: target.id, email: form.email, password: pwReset } });
      setPwReset("");
      setNote("Password reset. Hand the new password to the user.");
      await saved();
    } catch (err) { error(err.message); }
  };

  const issueLoginPass = async () => {
    try {
      const res = await apiRequest("create_login_pass", { method: "POST", body: { userId: target.id } });
      setLoginPass({ code: res.token, mins: res.expiresInMinutes });
    } catch (err) { error(err.message); }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (forced && form.password.length < 8) { error("Choose a new password of at least 8 characters."); return; }
    try {
      if (isSelf) {
        await apiRequest("update_self", { method: "POST", body: { name: form.name, rank: form.rank, email: form.email, password: form.password || undefined } });
      } else {
        const body = { userId: target.id, name: form.name, rank: form.rank, email: form.email };
        if (assoc.squadronId || assoc.flightId) { body.squadronId = assoc.squadronId; body.flightId = assoc.flightId; }
        await apiRequest("update_staff", { method: "POST", body });
      }
      await saved();
      close();
    } catch (err) {
      error(err.message);
    }
  };

  const flightAffiliations = (affiliations || []).filter((a) => a.flight_id && (a.role === "flight_admin" || a.role === "leader"));
  // One row per flight (flight_admin wins if both roles are present).
  const flightRoles = Object.values((affiliations || []).reduce((acc, a) => {
    if (!a.flight_id || (a.role !== "flight_admin" && a.role !== "leader")) return acc;
    if (!acc[a.flight_id]) acc[a.flight_id] = { flight_id: a.flight_id, flight_name: a.flight_name, role: a.role };
    if (a.role === "flight_admin") acc[a.flight_id].role = "flight_admin";
    return acc;
  }, {}));
  const [move, setMove] = useState({ from: "", to: "" });

  const changeRole = async (flightId, role) => {
    try {
      await apiRequest("change_flight_role", { method: "POST", body: { userId: target.id, flightId, role } });
      await saved();
    } catch (err) {
      error(err.message);
    }
  };

  const moveFlight = async () => {
    const source = move.from || (flightAffiliations[0] && `${flightAffiliations[0].role}:${flightAffiliations[0].flight_id}`);
    if (!source || !move.to) return;
    const [role, fromFlightId] = source.split(":");
    try {
      await apiRequest("move_staff_flight", { method: "POST", body: { userId: target.id, role, fromFlightId, toFlightId: move.to } });
      await saved();
      setMove({ from: "", to: "" });
    } catch (err) {
      error(err.message);
    }
  };

  const revoke = async (a) => {
    if (!window.confirm(`Remove "${affiliationLabel(a)}" from ${target.name}?`)) return;
    try {
      await apiRequest("revoke_role", { method: "POST", body: { userId: target.id, role: a.role, squadronId: a.squadron_id || "", flightId: a.flight_id || "" } });
      await saved();
    } catch (err) {
      error(err.message);
    }
  };

  const toggleStatus = async () => {
    const next = target.status === "disabled" ? "active" : "disabled";
    if (!window.confirm(`${next === "disabled" ? "Deactivate" : "Reactivate"} ${target.name}?`)) return;
    try {
      await apiRequest("set_staff_status", { method: "POST", body: { userId: target.id, status: next } });
      await saved();
      close();
    } catch (err) {
      error(err.message);
    }
  };

  const removeAccount = async () => {
    const warning = target.status === "disabled"
      ? `Permanently delete ${target.name}'s account AND all of its data — every event they created and the attendance, scores, and timing inside those events (for all members), plus any marks/scores/timing they recorded elsewhere. This cannot be undone.`
      : `Permanently delete ${target.name}'s account? This only works if they have no recorded history; deactivate first to fully purge. This cannot be undone.`;
    if (!window.confirm(warning)) return;
    try {
      await apiRequest("delete_staff", { method: "POST", body: { userId: target.id } });
      await saved();
      close();
    } catch (err) {
      error(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 p-4 flex items-center justify-center" onClick={forced ? undefined : close}>
      <form className="bg-white p-5 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto" onSubmit={submit} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h2 className="display text-3xl text-ink m-0">{forced ? "Set a New Password" : isSelf ? "Your Account" : "Edit User"}</h2>
          {!forced && (
            <button type="button" onClick={close} className="text-stone-600 bg-transparent border-0 cursor-pointer p-1" aria-label="Close"><X size={18} /></button>
          )}
        </div>
        {forced && (
          <div className="mono text-[10px] text-ink bg-signal-tint border border-signal px-3 py-2">
            An administrator requires you to set a new password before continuing.
          </div>
        )}
        {isSelf ? (
          <div>
            <MonoLabel className="text-[9px] block mb-1">Name</MonoLabel>
            <div className="text-[15px] font-semibold text-ink">{form.name}</div>
          </div>
        ) : (
          <Field placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        )}
        {!forced && <Field placeholder="Rank (optional)" value={form.rank} onChange={(e) => setForm({ ...form, rank: e.target.value })} />}
        <Field type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        {isSelf && (
          <Field type="password" minLength={8} placeholder="New password (optional)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        )}

        <MonoLabel className="text-[9px] block pt-1">Affiliations</MonoLabel>
        {affiliations?.length ? (
          <div className="flex flex-wrap gap-1.5">
            {affiliations.map((a, i) => (
              <span key={i} className="mono text-[9px] font-semibold uppercase tracking-[0.1em] px-2 py-1 bg-signal-tint text-signal-deep flex items-center gap-1.5">
                {affiliationLabel(a)}
                {!isSelf && (
                  <button type="button" onClick={() => revoke(a)} aria-label={`Remove ${affiliationLabel(a)}`} className="bg-transparent border-0 p-0 text-signal-deep cursor-pointer flex">
                    <X size={12} />
                  </button>
                )}
              </span>
            ))}
          </div>
        ) : (
          <div className="mono text-[10px] text-muted">No unit affiliations</div>
        )}

        {isSelf && !forced && scopeSquadrons?.length > 0 && (
          <>
            <MonoLabel className="text-[9px] block pt-1">Viewing — squadron / flight</MonoLabel>
            <Field as="select" value={scopeSquadron || ""} onChange={(e) => onScopeSquadron(e.target.value)}>
              {scopeSquadrons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Field>
            <Field as="select" value={flightScope || ""} onChange={(e) => onFlightScope(e.target.value)}>
              <option value="">All flights</option>
              {(scopeFlights || []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </Field>
          </>
        )}

        {isSelf && !forced && <AccountFitness />}

        {!isSelf && target.status === "disabled" && (
          <div className="mono text-[10px] font-semibold uppercase tracking-[0.1em] text-fail">Account deactivated</div>
        )}
        {!isSelf && (
          <button type="button" onClick={toggleStatus} className={`w-full mono text-[10px] font-semibold uppercase tracking-[0.12em] py-2 border cursor-pointer bg-transparent ${target.status === "disabled" ? "border-pass text-pass-deep" : "border-fail text-fail"}`}>
            {target.status === "disabled" ? "Reactivate account" : "Deactivate account"}
          </button>
        )}
        {!isSelf && (
          <button type="button" onClick={removeAccount} className="w-full mono text-[10px] font-semibold uppercase tracking-[0.12em] py-2 border border-fail text-fail cursor-pointer bg-transparent">
            Delete account
          </button>
        )}

        {!isSelf && (
          <>
            <MonoLabel className="text-[9px] block pt-1">Reset password</MonoLabel>
            {note && <div className="mono text-[10px] text-ink bg-signal-tint border border-signal px-2 py-1.5">{note}</div>}
            <div className="flex gap-2">
              <Field type="password" minLength={8} className="flex-1" placeholder="New password (min 8)" value={pwReset} onChange={(e) => setPwReset(e.target.value)} />
              <Button type="button" variant="outline" disabled={pwReset.length < 8} onClick={resetPassword}>Reset</Button>
            </div>
          </>
        )}

        {canIssueLoginPass && (
          <>
            <MonoLabel className="text-[9px] block pt-1">One-time login barcode</MonoLabel>
            {loginPass ? (
              <IssuedBarcode label="Login barcode" code={loginPass.code} url={scanUrl("login", loginPass.code)} hint={`single use · ${loginPass.mins} min`} onDismiss={() => setLoginPass(null)} />
            ) : (
              <Button type="button" variant="outline" className="w-full" onClick={issueLoginPass}>
                <span className="flex items-center justify-center gap-1.5"><QrCode size={14} /> Generate login barcode</span>
              </Button>
            )}
            <div className="mono text-[9px] text-muted">Logs {target.name} in once without a password, to fix account issues.</div>
          </>
        )}

        {!isSelf && isPlatform && (
          <>
            <MonoLabel className="text-[9px] block pt-1">Associate with unit (grants scoped admin)</MonoLabel>
            <Field as="select" value={assoc.squadronId} onChange={(e) => setAssoc({ squadronId: e.target.value, flightId: "" })}>
              <option value="">— Select squadron —</option>
              {squadrons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Field>
            {assoc.squadronId && (
              <Field as="select" value={assoc.flightId} onChange={(e) => setAssoc({ ...assoc, flightId: e.target.value })}>
                <option value="">Whole squadron</option>
                {flights.filter((f) => f.squadron_id === assoc.squadronId).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </Field>
            )}
          </>
        )}

        {!isSelf && flightRoles.length > 0 && (
          <>
            <MonoLabel className="text-[9px] block pt-1">Change flight role</MonoLabel>
            {flightRoles.map((fr) => (
              <div key={fr.flight_id} className="flex gap-2 items-center">
                <span className="flex-1 text-[13px] text-ink truncate">{fr.flight_name}</span>
                <Field as="select" className="w-auto" value={fr.role} onChange={(e) => changeRole(fr.flight_id, e.target.value)}>
                  <option value="leader">Leader</option>
                  <option value="flight_admin">Flight Admin</option>
                </Field>
              </div>
            ))}
          </>
        )}

        {!isSelf && flightAffiliations.length > 0 && (
          <>
            <MonoLabel className="text-[9px] block pt-1">Move to another flight</MonoLabel>
            {flightAffiliations.length > 1 && (
              <Field as="select" value={move.from} onChange={(e) => setMove({ ...move, from: e.target.value })}>
                {flightAffiliations.map((a) => <option key={`${a.role}:${a.flight_id}`} value={`${a.role}:${a.flight_id}`}>{affiliationLabel(a)}</option>)}
              </Field>
            )}
            <div className="flex gap-2">
              <Field as="select" className="flex-1" value={move.to} onChange={(e) => setMove({ ...move, to: e.target.value })}>
                <option value="">— Destination flight —</option>
                {(flights || []).map((f) => <option key={f.id} value={f.id}>{f.squadron_name ? `${f.squadron_name} · ` : ""}{f.name}</option>)}
              </Field>
              <Button type="button" disabled={!move.to} onClick={moveFlight}>Move</Button>
            </div>
          </>
        )}
        <Button className="w-full">Save</Button>
      </form>
    </div>
  );
}

// Rename / archive / delete the scoped squadron and its flights.
// Set the squadron's weather location. Geocodes the typed place (Open-Meteo, free,
// no key) and stores the coords; emptying the field clears it. Squadron and platform
// admins both reach this (gated by canRole, which treats platform_admin as squadron).
function WeatherLocationTool({ squadron, run }) {
  const [wxPlace, setWxPlace] = useState(squadron?.weather_place || "");
  const [wxMsg, setWxMsg] = useState("");
  const saveWeather = async () => {
    const place = wxPlace.trim();
    if (!place) { await run("update_squadron", { squadronId: squadron.id, weatherPlace: "" }); setWxMsg("Weather location cleared."); return; }
    setWxMsg("Looking up…");
    try {
      const geo = await fetch(`https://geocoding-api.open-meteo.com/v1/search?count=1&name=${encodeURIComponent(place)}`).then((r) => r.json());
      const hit = geo.results?.[0];
      if (!hit) { setWxMsg("Couldn't find that place — try a city or \"City, State\"."); return; }
      const label = [hit.name, hit.admin1, hit.country_code].filter(Boolean).join(", ");
      await run("update_squadron", { squadronId: squadron.id, weatherLat: hit.latitude, weatherLon: hit.longitude, weatherPlace: label });
      setWxPlace(label);
      setWxMsg(`Saved: ${label}`);
    } catch { setWxMsg("Lookup failed — check your connection."); }
  };
  return (
    <div className="space-y-2">
      <div className="mono text-[10px] text-muted">Shows the forecast on {squadron?.name || "this squadron"}'s sessions within 10 days. Enter a city or "City, State".</div>
      <div className="flex gap-2">
        <Field className="flex-1" placeholder="e.g. Colorado Springs, CO" value={wxPlace} onChange={(e) => setWxPlace(e.target.value)} />
        <Button type="button" onClick={saveWeather}>Save</Button>
      </div>
      {wxMsg && <div className="mono text-[10px] text-muted">{wxMsg}</div>}
    </div>
  );
}

function UnitManager({ squadron, flights, isPlatform, run }) {
  const [sq, setSq] = useState({ name: squadron?.name || "", abbreviation: squadron?.abbreviation || "" });
  const [names, setNames] = useState(() => Object.fromEntries((flights || []).map((f) => [f.id, f.name])));
  if (!squadron) return <div className="mono text-[10px] text-muted">Select a squadron first.</div>;
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <MonoLabel className="text-[9px] block">Squadron</MonoLabel>
        <Field placeholder="Squadron name" value={sq.name} onChange={(e) => setSq({ ...sq, name: e.target.value })} />
        <Field placeholder="Abbreviation" value={sq.abbreviation} onChange={(e) => setSq({ ...sq, abbreviation: e.target.value })} />
        <div className="flex gap-2">
          <Button type="button" className="flex-1" onClick={() => run("update_squadron", { squadronId: squadron.id, name: sq.name, abbreviation: sq.abbreviation })}>Save squadron</Button>
          <Button type="button" variant="outline" onClick={() => { if (window.confirm(`Archive squadron "${squadron.name}"? It stays in history but disappears from active lists.`)) run("update_squadron", { squadronId: squadron.id, status: "archived" }); }}>Archive</Button>
          {isPlatform && (
            <Button type="button" variant="danger" onClick={() => { if (window.confirm(`Delete squadron "${squadron.name}"? Only works if it has no flights or events. This cannot be undone.`)) run("delete_squadron", { squadronId: squadron.id }); }}>Delete</Button>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <MonoLabel className="text-[9px] block">Flights</MonoLabel>
        {(flights || []).length === 0 && <div className="mono text-[10px] text-muted">No flights.</div>}
        {(flights || []).map((f) => (
          <div key={f.id} className="flex gap-2 items-center">
            <Field className="flex-1" value={names[f.id] ?? ""} onChange={(e) => setNames({ ...names, [f.id]: e.target.value })} />
            <Button type="button" onClick={() => run("update_flight", { flightId: f.id, name: names[f.id] })}>Save</Button>
            <Button type="button" variant="outline" onClick={() => { if (window.confirm(`Archive flight "${f.name}"?`)) run("update_flight", { flightId: f.id, status: "archived" }); }}>Archive</Button>
            <Button type="button" variant="danger" onClick={() => { if (window.confirm(`Delete flight "${f.name}"? Only works if it has no events or members. This cannot be undone.`)) run("delete_flight", { flightId: f.id }); }}>Delete</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApprovalCard({ item }) {
  return (
    <div className="bg-white mb-3">
      <div className="flex items-stretch gap-3 px-4 py-3.5">
        <span className="w-1 flex-shrink-0" style={{ background: item.color }} />
        <div className="flex-1 min-w-0">
          <div className="mono text-[9px] uppercase tracking-[0.14em] mb-1" style={{ color: item.color }}>{item.kind}</div>
          <div className="text-base font-semibold text-ink">{item.name}</div>
          {item.detail && <div className="mono text-[10px] text-muted mt-0.5">{item.detail}</div>}
          {item.extra}
        </div>
      </div>
      <div className="flex gap-px bg-line border-t border-line">
        {item.decline && (
          <button onClick={item.decline} className="flex-1 h-11 bg-white border-0 text-red-700 mono text-[11px] font-semibold uppercase tracking-[0.12em] cursor-pointer flex items-center justify-center gap-2">
            <X size={14} />Decline
          </button>
        )}
        <button onClick={item.approve} className="flex-1 h-11 bg-ink border-0 text-white mono text-[11px] font-semibold uppercase tracking-[0.12em] cursor-pointer flex items-center justify-center gap-2">
          <Check size={14} className="text-signal-bright" />{item.approveLabel || "Approve"}
        </button>
      </div>
    </div>
  );
}

const matchesMember = (member, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return `${member.display_name} ${member.rank_label || ""} ${member.flight_name || ""}`.toLowerCase().includes(q);
};

// One active member with inline edit / move-flight / merge / delete controls.
function MemberRow({ member, flights, others, principal, run, error }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ displayName: member.display_name, rank: member.rank_label || "" });
  const [toFlightId, setToFlightId] = useState(member.home_flight_id);
  const [mergeId, setMergeId] = useState("");
  const [role, setRole] = useState("leader");
  const [resetToken, setResetToken] = useState(null);
  const issueReset = async () => {
    try {
      const res = await apiRequest("issue_member_reset", { method: "POST", body: { accountId: member.account_id } });
      setResetToken(res.token);
    } catch (err) { error(err.message); }
  };
  const sameSquadronFlights = flights.filter((f) => f.squadron_id === member.squadron_id);
  const mergeable = (others || []).filter((m) => m.id !== member.id && m.squadron_id === member.squadron_id);
  // Promotable only if they enrolled with an email login (used as the staff email).
  const canPromote = (member.account_username || "").includes("@");

  return (
    <div className="border-b border-line-soft last:border-b-0 pb-2">
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={() => setOpen(!open)} className="flex-1 min-w-0 text-left bg-transparent border-0 p-0 cursor-pointer">
          <span className="text-sm text-ink truncate">{member.display_name} <span className="mono text-[10px] text-muted">{member.rank_label ? `${member.rank_label} · ` : ""}{member.flight_name}</span></span>
        </button>
        <Button size="sm" variant="outline" onClick={() => { if (window.confirm(`Deactivate ${member.display_name}? Their login is disabled and they leave active rosters; history is kept.`)) run("set_member_status", { memberId: member.id, status: "inactive" }); }}>Deactivate</Button>
      </div>
      {open && (
        <div className="flex flex-col gap-2 mt-2 pl-1">
          <div className="grid grid-cols-2 gap-2">
            <Field placeholder="Display name" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
            <Field placeholder="Rank (optional)" value={form.rank} onChange={(e) => setForm({ ...form, rank: e.target.value })} />
          </div>
          <Button size="sm" variant="outline" disabled={!form.displayName.trim()} onClick={() => run("update_member", { memberId: member.id, displayName: form.displayName, rank: form.rank })}>Save details</Button>

          {sameSquadronFlights.length > 1 && (
            <div className="flex gap-2">
              <Field as="select" className="flex-1 h-9 text-sm" value={toFlightId} onChange={(e) => setToFlightId(e.target.value)}>
                {sameSquadronFlights.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </Field>
              <Button size="sm" variant="outline" disabled={toFlightId === member.home_flight_id} onClick={() => run("move_member", { memberId: member.id, toFlightId })}>Move</Button>
            </div>
          )}

          {mergeable.length > 0 && (
            <div className="flex gap-2">
              <Field as="select" className="flex-1 h-9 text-sm" value={mergeId} onChange={(e) => setMergeId(e.target.value)}>
                <option value="">Merge into…</option>
                {mergeable.map((m) => <option key={m.id} value={m.id}>{m.display_name} · {m.flight_name}</option>)}
              </Field>
              <Button size="sm" variant="outline" disabled={!mergeId} onClick={() => { if (window.confirm(`Merge ${member.display_name} into the selected member? This moves their history and cannot be undone.`)) run("merge_member", { sourceMemberId: member.id, targetMemberId: mergeId }); }}>Merge</Button>
            </div>
          )}

          <div className="border-t border-line-soft pt-2 mt-1">
            <MonoLabel className="text-[9px] block mb-1">Make staff</MonoLabel>
            {canPromote ? (
              <div className="flex gap-2">
                <Field as="select" className="flex-1 h-9 text-sm" value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="leader">Leader</option>
                  <option value="flight_admin">Flight Admin</option>
                  {canRole(principal, "squadron_admin") && <option value="squadron_admin">Squadron Admin</option>}
                  {canRole(principal, "platform_admin") && <option value="platform_admin">Platform Admin</option>}
                </Field>
                <Button size="sm" onClick={() => { if (window.confirm(`Make ${member.display_name} ${ROLE_LABELS[role]}? They keep their email and password and move to the staff roster.`)) run("promote_member", { accountId: member.account_id, role, squadronId: role === "platform_admin" ? "" : member.squadron_id, flightId: role === "platform_admin" || role === "squadron_admin" ? "" : member.home_flight_id }); }}>Grant role</Button>
              </div>
            ) : (
              <div className="mono text-[10px] text-muted">No email login — enrol them with a barcode invite to enable staff promotion.</div>
            )}
          </div>

          {member.account_id && (
            <div className="border-t border-line-soft pt-2 mt-1">
              <MonoLabel className="text-[9px] block mb-1">Password reset</MonoLabel>
              {resetToken ? (
                <IssuedBarcode label="Password reset" code={scanUrl("reset", resetToken)} url={scanUrl("reset", resetToken)} hint="single use · 24h" onDismiss={() => setResetToken(null)} />
              ) : (
                <Button size="sm" variant="outline" className="w-full" onClick={issueReset}>
                  <span className="flex items-center justify-center gap-1.5"><QrCode size={14} /> Generate reset barcode</span>
                </Button>
              )}
              <div className="mono text-[9px] text-muted mt-1">They scan it to set a new password — no current password needed.</div>
            </div>
          )}

          <Button size="sm" variant="danger" onClick={() => {
            const isPlatform = principal.roles?.some((r) => r.role === "platform_admin");
            const msg = isPlatform
              ? `Permanently delete ${member.display_name} AND all of their data — every registration, run result, and assessment. This cannot be undone.`
              : `Permanently delete ${member.display_name}? Only works if they have no history; otherwise deactivate. This cannot be undone.`;
            if (window.confirm(msg)) run("delete_member", { memberId: member.id });
          }}>Delete member</Button>
        </div>
      )}
    </div>
  );
}

function ToolRow({ icon: ToolIcon, label, open, onToggle, children }) {
  return (
    <div className="border-b border-line-soft last:border-b-0">
      <button onClick={onToggle} className="w-full flex items-center gap-3.5 px-4 py-3.5 bg-white border-0 cursor-pointer text-left">
        <ToolIcon size={19} className="text-ink flex-shrink-0" />
        <span className="flex-1 text-[15px] font-medium text-ink">{label}</span>
        <ChevronRight size={18} className={`text-[#c4c0b9] transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-1 bg-white">{children}</div>}
    </div>
  );
}

function AdminPanel({ principal, context: fullContext, scopeId, flightScope, reload, error }) {
  // Platform admins receive every unit; scope the view to one squadron so they
  // actively switch rather than seeing all units at once. Platform-level
  // approvals (new squadron requests, shares) pass through unscoped. When a
  // flight is also selected, narrow the member rosters to that flight (members
  // carry home_flight_id; member accounts carry flight_id).
  const context = useMemo(() => {
    let ctx = fullContext;
    if (scopeId) {
      const inScope = (x) => x?.squadron_id === scopeId;
      ctx = {
        ...ctx,
        squadrons: (ctx.squadrons || []).filter((s) => s.id === scopeId),
        flights: (ctx.flights || []).filter(inScope),
        staff: (ctx.staff || []).filter(inScope),
        provisionalMembers: (ctx.provisionalMembers || []).filter(inScope),
        activeMembers: (ctx.activeMembers || []).filter(inScope),
        inactiveMembers: (ctx.inactiveMembers || []).filter(inScope),
        memberAccountRequests: (ctx.memberAccountRequests || []).filter(inScope)
      };
    }
    if (flightScope) {
      const inFlight = (x) => (x?.home_flight_id || x?.flight_id) === flightScope;
      ctx = {
        ...ctx,
        provisionalMembers: (ctx.provisionalMembers || []).filter(inFlight),
        activeMembers: (ctx.activeMembers || []).filter(inFlight),
        inactiveMembers: (ctx.inactiveMembers || []).filter(inFlight),
        memberAccountRequests: (ctx.memberAccountRequests || []).filter(inFlight)
      };
    }
    return ctx;
  }, [fullContext, scopeId, flightScope]);
  const administrableFlights = context.flights.filter((item) => canAdminFlight(principal, item));
  // Flights this user (leader or above) may mint an invite for — mirrors the
  // server's is_flight_staff check.
  const inviteFlights = context.flights.filter((f) =>
    principal.roles.some((r) =>
      r.role === "platform_admin" ||
      (r.role === "squadron_admin" && r.squadron_id === f.squadron_id) ||
      ((r.role === "flight_admin" || r.role === "leader") && r.flight_id === f.id)
    )
  );
  const [flight, setFlight] = useState({ squadronId: context.squadrons[0]?.id || "", name: "" });
  const [staff, setStaff] = useState({ squadronId: administrableFlights[0]?.squadron_id || context.squadrons[0]?.id || "", flightId: administrableFlights[0]?.id || "", name: "", email: "", password: "", role: "leader" });
  const [password, setPassword] = useState({ scope: "flight", squadronId: context.squadrons[0]?.id || "", flightId: administrableFlights[0]?.id || "", password: "" });
  const [mergeTargets, setMergeTargets] = useState({});
  const [memberSearch, setMemberSearch] = useState("");
  const [openTool, setOpenTool] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [assign, setAssign] = useState({ requestId: null, role: "flight_admin", squadronId: "", flightId: "" });
  const [invite, setInvite] = useState({ flightId: "", expiresInHours: 72, maxUses: 1 });
  const [inviteToken, setInviteToken] = useState(null);

  const issueInvite = async () => {
    const flightId = invite.flightId || inviteFlights[0]?.id;
    if (!flightId) return;
    try {
      const res = await apiRequest("create_flight_invite", { method: "POST", body: {
        flightId, expiresInHours: invite.expiresInHours, maxUses: invite.maxUses || undefined
      } });
      setInviteToken({ code: res.token, hours: res.expiresInHours, maxUses: res.maxUses });
    } catch (err) { error(err.message); }
  };

  const run = async (action, body) => {
    try {
      await apiRequest(action, { method: "POST", body });
      await reload();
    } catch (err) {
      error(err.message);
    }
  };

  const [linkMsg, setLinkMsg] = useState("");
  const linkStaffMembers = async () => {
    try {
      const res = await apiRequest("link_staff_members", { method: "POST", body: {} });
      setLinkMsg(`Linked ${res.linked} account${res.linked === 1 ? "" : "s"}${res.skipped ? ` · ${res.skipped} ambiguous skipped` : ""}.`);
      await reload();
    } catch (err) {
      error(err.message);
    }
  };

  const isAdminAny = principal.roles.some((role) => ["platform_admin", "squadron_admin", "flight_admin"].includes(role.role));
  const isPlatform = principal.roles.some((role) => role.role === "platform_admin");
  const topRole = ["platform_admin", "squadron_admin", "flight_admin", "leader"]
    .find((role) => principal.roles.some((entry) => entry.role === role));

  const approvals = [
    ...(context.squadronRequests || []).map((request) => ({
      id: `squadron-${request.id}`,
      kind: request.created_squadron_id ? "Access request" : "Squadron request",
      color: "oklch(0.55 0.16 47)",
      name: request.created_squadron_id
        ? `${request.requester_name} → ${request.target_squadron_name}`
        : `${request.squadron_name} (${request.abbreviation})`,
      detail: request.created_squadron_id
        ? `${request.requester_email} · Requesting flight admin: ${request.first_flight_name}`
        : `${request.requester_name} · ${request.requester_email} · First flight: ${request.first_flight_name}`,
      approveLabel: request.created_squadron_id ? "Grant access" : "Approve",
      approve: () => run("approve_squadron", { requestId: request.id }),
      decline: () => run("reject_squadron", { requestId: request.id }),
      extra: assign.requestId === request.id ? (
        <div className="flex flex-col gap-2 mt-2.5">
          <Field as="select" value={assign.role} onChange={(e) => setAssign({ ...assign, role: e.target.value, flightId: "" })}>
            <option value="flight_admin">Flight Admin</option>
            <option value="leader">Leader</option>
            {canRole(principal, "squadron_admin") && <option value="squadron_admin">Squadron Admin</option>}
          </Field>
          <Field as="select" value={assign.squadronId} onChange={(e) => setAssign({ ...assign, squadronId: e.target.value, flightId: "" })}>
            <option value="">— Squadron —</option>
            {fullContext.squadrons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Field>
          {assign.role !== "squadron_admin" && (
            <Field as="select" value={assign.flightId} onChange={(e) => setAssign({ ...assign, flightId: e.target.value })}>
              <option value="">— Flight —</option>
              {(fullContext.flights || []).filter((f) => f.squadron_id === assign.squadronId).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </Field>
          )}
          <div className="flex gap-2">
            <Button size="sm" variant="outline" type="button" onClick={() => setAssign({ requestId: null, role: "flight_admin", squadronId: "", flightId: "" })}>Cancel</Button>
            <Button
              size="sm"
              type="button"
              disabled={!assign.squadronId || (assign.role !== "squadron_admin" && !assign.flightId)}
              onClick={() => { run("assign_request", { requestId: request.id, role: assign.role, squadronId: assign.squadronId, flightId: assign.flightId }); setAssign({ requestId: null, role: "flight_admin", squadronId: "", flightId: "" }); }}
            >
              Assign
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAssign({ requestId: request.id, role: "flight_admin", squadronId: fullContext.squadrons[0]?.id || "", flightId: "" })}
          className="mono text-[10px] font-semibold uppercase tracking-[0.08em] underline text-signal-deep bg-transparent border-0 cursor-pointer p-0 mt-1.5"
        >
          Assign to existing unit instead
        </button>
      )
    })),
    ...(context.pendingShares || []).map((share) => ({
      id: `share-${share.id}`,
      kind: "Event share",
      color: "oklch(0.55 0.12 250)",
      name: share.title,
      detail: `${share.owner_flight_name} → ${share.recipient_flight_name} · ${formatDateTime(share.starts_at)} · ${share.attendance_count} registered`,
      approve: () => run("review_share", { shareId: share.id, decision: "accept" }),
      decline: () => run("review_share", { shareId: share.id, decision: "reject" })
    })),
    ...(context.memberAccountRequests || []).map((request) => ({
      id: `account-${request.id}`,
      kind: "Member account",
      color: "oklch(0.5 0.13 150)",
      name: `${request.display_name}${request.rank_label ? ` · ${request.rank_label}` : ""}`,
      detail: `${request.flight_name} · username ${request.username}`,
      approve: () => run("approve_member_account", { accountId: request.id })
    })),
    ...(context.provisionalMembers || []).map((member) => ({
      id: `member-${member.id}`,
      kind: "Member profile",
      color: "oklch(0.55 0.14 80)",
      name: `${member.display_name}${member.rank_label ? ` · ${member.rank_label}` : ""}`,
      detail: member.flight_name,
      approveLabel: "Approve profile",
      approve: () => run("approve_member", { memberId: member.id }),
      extra: context.activeMembers?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2.5">
          <Field
            as="select"
            className="flex-1 min-w-[12rem] h-9 text-sm"
            value={mergeTargets[member.id] || ""}
            onChange={(e) => setMergeTargets({ ...mergeTargets, [member.id]: e.target.value })}
          >
            <option value="">Merge into existing member...</option>
            {context.activeMembers.filter((target) => target.squadron_id === member.squadron_id).map((target) => (
              <option key={target.id} value={target.id}>{target.display_name} · {target.flight_name}</option>
            ))}
          </Field>
          <Button size="sm" variant="outline" disabled={!mergeTargets[member.id]} onClick={() => run("merge_member", { sourceMemberId: member.id, targetMemberId: mergeTargets[member.id] })}>
            Merge
          </Button>
        </div>
      )
    }))
  ];

  return (
    <div>
      <div className="bg-ink -mx-4 -mt-5 px-5 pt-4 pb-4 mb-5 flex items-center justify-between">
        <div>
          <h2 className="display text-[34px] leading-[0.9] text-white m-0">ADMIN PANEL</h2>
          <div className="mono text-[9px] uppercase tracking-[0.16em] text-stone-500 mt-1">
            {context.squadrons[0]?.name || "Platform"} · {ROLE_LABELS[topRole] || "Staff"}
          </div>
        </div>
        <Shield size={24} className="text-signal-bright" />
      </div>

      <div className="flex items-center justify-between mb-3">
        <MonoLabel className="text-[10px] font-semibold text-ink">Approvals</MonoLabel>
        {approvals.length > 0 && (
          <span className="mono text-[9px] font-semibold uppercase tracking-[0.1em] text-ink bg-signal px-2 py-1">
            {approvals.length} pending
          </span>
        )}
      </div>
      {approvals.length === 0 ? (
        <div className="bg-white px-4 py-5 mb-6 text-sm text-muted">Nothing waiting for review.</div>
      ) : (
        <div className="mb-6">
          {approvals.map((item) => <ApprovalCard key={item.id} item={item} />)}
        </div>
      )}

      {context.staff?.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3">
            <MonoLabel className="text-[10px] font-semibold text-ink">Staff &amp; leaders</MonoLabel>
            {isAdminAny && (
              <button
                onClick={() => setOpenTool(openTool === "addStaff" ? null : "addStaff")}
                className="flex items-center gap-1.5 bg-transparent border border-[#c4c0b9] px-2.5 py-1.5 mono text-[9px] font-semibold uppercase tracking-[0.1em] text-ink cursor-pointer"
              >
                <Plus size={13} />Add
              </button>
            )}
          </div>
          <div className="bg-white mb-6">
            {openTool === "addStaff" && (
              <form className="p-4 border-b border-line space-y-3" onSubmit={(e) => { e.preventDefault(); run("create_staff", staff); setOpenTool(null); }}>
                <Field placeholder="Name" value={staff.name} onChange={(e) => setStaff({ ...staff, name: e.target.value })} />
                <Field type="email" placeholder="Email" value={staff.email} onChange={(e) => setStaff({ ...staff, email: e.target.value })} />
                <Field type="password" minLength={8} placeholder="Initial password" value={staff.password} onChange={(e) => setStaff({ ...staff, password: e.target.value })} />
                <Field as="select" value={staff.role} onChange={(e) => setStaff({ ...staff, role: e.target.value })}>
                  <option value="leader">Leader</option>
                  <option value="flight_admin">Flight Admin</option>
                  {canRole(principal, "squadron_admin") && <option value="squadron_admin">Squadron Admin</option>}
                  {canRole(principal, "platform_admin") && <option value="platform_admin">Platform Admin</option>}
                </Field>
                <Field as="select" value={staff.squadronId} onChange={(e) => setStaff({ ...staff, squadronId: e.target.value })}>
                  {staff.role === "platform_admin" && <option value="">Platform-wide (no unit)</option>}
                  {context.squadrons.map((squadron) => <option key={squadron.id} value={squadron.id}>{squadron.name}</option>)}
                </Field>
                {staff.role !== "squadron_admin" && (
                  <Field as="select" value={staff.flightId} onChange={(e) => setStaff({ ...staff, flightId: e.target.value })}>
                    {staff.role === "platform_admin" && <option value="">Whole squadron / none</option>}
                    {administrableFlights.filter((item) => item.squadron_id === staff.squadronId).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </Field>
                )}
                <Button className="w-full">Add Staff</Button>
              </form>
            )}
            {Object.values((context.staff || []).reduce((acc, p) => {
              if (!acc[p.id] || (ROLE_RANK[p.role] || 0) > (ROLE_RANK[acc[p.id].role] || 0)) acc[p.id] = p;
              return acc;
            }, {})).sort((a, b) =>
              (ROLE_RANK[b.role] || 0) - (ROLE_RANK[a.role] || 0) || (a.name || "").localeCompare(b.name || "")
            ).map((person) => (
              <div key={person.id} className="flex items-center gap-3 px-4 py-3 border-b border-line-soft last:border-b-0">
                <button
                  type="button"
                  aria-label={`Edit ${person.name}`}
                  onClick={() => setEditUser(person)}
                  className="bg-transparent border-0 p-0 cursor-pointer flex"
                >
                  <Avatar initials={initialsOf(person.name)} size={38} tone="light" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`text-[15px] font-semibold truncate ${person.status === "disabled" ? "text-muted line-through" : "text-ink"}`}>{person.rank_label ? `${person.rank_label} ` : ""}{person.name}</div>
                  <div className="mono text-[10px] text-muted truncate">{person.status === "disabled" ? "Deactivated · " : ""}{person.email}</div>
                </div>
                <span className={`mono text-[9px] font-semibold uppercase tracking-[0.1em] px-2 py-1 flex-shrink-0 ${
                  person.role === "leader" ? "bg-signal-tint text-signal-deep" : "bg-ink text-white"
                }`}>
                  {ROLE_LABELS[person.role] || person.role}
                </span>
                <button
                  type="button"
                  aria-label={`Edit ${person.name}`}
                  onClick={() => setEditUser(person)}
                  className="bg-transparent border-0 text-muted cursor-pointer p-1 flex flex-shrink-0"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <MonoLabel className="text-[10px] font-semibold text-ink block mb-3">Tools</MonoLabel>
      <div className="bg-white">
        {inviteFlights.length > 0 && (
          <ToolRow icon={QrCode} label="Flight invite barcode" open={openTool === "invite"} onToggle={() => setOpenTool(openTool === "invite" ? null : "invite")}>
            <div className="space-y-3">
              <p className="mono text-[10px] text-muted">Generate a barcode that lets a new person enrol in a flight. The account stays pending your approval, and every enrolment is traced to you.</p>
              <Field as="select" value={invite.flightId || inviteFlights[0]?.id} onChange={(e) => setInvite({ ...invite, flightId: e.target.value })}>
                {inviteFlights.map((f) => <option key={f.id} value={f.id}>{f.squadron_name ? `${f.squadron_name} · ` : ""}{f.name}</option>)}
              </Field>
              <div className="flex gap-2">
                <div className="flex-1">
                  <MonoLabel className="text-[9px] block mb-1">Expires (hours)</MonoLabel>
                  <Field type="number" min={1} max={720} value={invite.expiresInHours} onChange={(e) => setInvite({ ...invite, expiresInHours: e.target.value })} />
                </div>
                <div className="flex-1">
                  <MonoLabel className="text-[9px] block mb-1">Max uses (blank = ∞)</MonoLabel>
                  <Field type="number" min={1} max={500} placeholder="∞" value={invite.maxUses} onChange={(e) => setInvite({ ...invite, maxUses: e.target.value })} />
                </div>
              </div>
              <Button type="button" className="w-full" onClick={issueInvite}>
                <span className="flex items-center justify-center gap-1.5"><QrCode size={14} /> Generate invite barcode</span>
              </Button>
              {inviteToken && (
                <IssuedBarcode label="Flight invite" code={inviteToken.code} url={scanUrl("invite", inviteToken.code)} hint={`${inviteToken.hours}h · ${inviteToken.maxUses ? `${inviteToken.maxUses} use${inviteToken.maxUses > 1 ? "s" : ""}` : "unlimited"}`} onDismiss={() => setInviteToken(null)} />
              )}
            </div>
          </ToolRow>
        )}
        {isAdminAny && (
          <ToolRow icon={Lock} label="Rotate viewer password" open={openTool === "password"} onToggle={() => setOpenTool(openTool === "password" ? null : "password")}>
            <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); run("rotate_viewer_password", password); setOpenTool(null); }}>
              <Field as="select" value={password.scope} onChange={(e) => setPassword({ ...password, scope: e.target.value })}>
                <option value="flight">Flight password</option>
                {canRole(principal, "squadron_admin") && <option value="squadron">Squadron password</option>}
              </Field>
              {password.scope === "flight" ? (
                <Field as="select" value={password.flightId} onChange={(e) => setPassword({ ...password, flightId: e.target.value })}>
                  {administrableFlights.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </Field>
              ) : (
                <Field as="select" value={password.squadronId} onChange={(e) => setPassword({ ...password, squadronId: e.target.value })}>
                  {context.squadrons.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </Field>
              )}
              <Field type="password" minLength={6} placeholder="New password" value={password.password} onChange={(e) => setPassword({ ...password, password: e.target.value })} />
              <Button className="w-full">Rotate and Invalidate Devices</Button>
            </form>
          </ToolRow>
        )}

        {canRole(principal, "squadron_admin") && (
          <ToolRow icon={Home} label="Create flight" open={openTool === "flight"} onToggle={() => setOpenTool(openTool === "flight" ? null : "flight")}>
            <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); run("create_flight", flight); setOpenTool(null); }}>
              <Field as="select" value={flight.squadronId} onChange={(e) => setFlight({ ...flight, squadronId: e.target.value })}>
                {context.squadrons.map((squadron) => <option key={squadron.id} value={squadron.id}>{squadron.name}</option>)}
              </Field>
              <Field placeholder="Flight name" value={flight.name} onChange={(e) => setFlight({ ...flight, name: e.target.value })} />
              <Button className="w-full">Create</Button>
            </form>
          </ToolRow>
        )}

        {canRole(principal, "squadron_admin") && context.squadrons[0] && (
          <ToolRow icon={Home} label="Rename / archive units" open={openTool === "units"} onToggle={() => setOpenTool(openTool === "units" ? null : "units")}>
            <UnitManager key={context.squadrons[0].id} squadron={context.squadrons[0]} flights={context.flights} isPlatform={isPlatform} run={run} />
          </ToolRow>
        )}

        {canRole(principal, "squadron_admin") && context.squadrons[0] && (
          <ToolRow icon={MapPin} label="Weather location" open={openTool === "weather"} onToggle={() => setOpenTool(openTool === "weather" ? null : "weather")}>
            <WeatherLocationTool key={context.squadrons[0].id} squadron={context.squadrons[0]} run={run} />
          </ToolRow>
        )}

        {(context.activeMembers?.length > 0 || context.inactiveMembers?.length > 0) && (
          <ToolRow icon={User} label="Manage members" open={openTool === "members"} onToggle={() => setOpenTool(openTool === "members" ? null : "members")}>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-3 pb-1">
                <span className="mono text-[10px] text-muted">{linkMsg || "Link staff to their merged member so self-registration reuses it."}</span>
                <Button size="sm" variant="outline" onClick={linkStaffMembers}>Link staff</Button>
              </div>
              <Field placeholder="Search members…" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} />
              {context.activeMembers?.filter((m) => matchesMember(m, memberSearch)).sort((a, b) => (a.display_name || "").localeCompare(b.display_name || "")).map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  flights={context.flights}
                  others={context.activeMembers}
                  principal={principal}
                  run={run}
                  error={error}
                />
              ))}
              {context.inactiveMembers?.filter((m) => matchesMember(m, memberSearch)).sort((a, b) => (a.display_name || "").localeCompare(b.display_name || "")).map((member) => (
                <div key={member.id} className="flex items-center justify-between gap-3">
                  <span className="text-sm text-muted line-through truncate">{member.display_name} <span className="mono text-[10px] text-muted no-underline">{member.flight_name}</span></span>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" onClick={() => run("set_member_status", { memberId: member.id, status: "active" })}>Reactivate</Button>
                    {isPlatform && (
                      <Button size="sm" variant="danger" onClick={() => { if (window.confirm(`Permanently delete ${member.display_name} AND all of their data — every registration, run result, and assessment. This cannot be undone.`)) run("delete_member", { memberId: member.id }); }}>Delete</Button>
                    )}
                  </div>
                </div>
              ))}
              {!context.activeMembers?.length && !context.inactiveMembers?.length && (
                <div className="mono text-[10px] text-muted">No members.</div>
              )}
            </div>
          </ToolRow>
        )}

        {context.auditLog?.length > 0 && (
          <ToolRow icon={FileText} label="Activity log" open={openTool === "audit"} onToggle={() => setOpenTool(openTool === "audit" ? null : "audit")}>
            <div className="flex flex-col">
              {context.auditLog.map((entry, i) => (
                <div key={i} className="flex items-baseline justify-between gap-3 py-1.5 border-b border-line-soft last:border-b-0">
                  <div className="min-w-0">
                    <span className="text-[13px] text-ink">{entry.actor_label}</span>
                    <span className="mono text-[10px] text-muted"> {entry.action.replace(/_/g, " ")} · {entry.entity_type}</span>
                  </div>
                  <span className="mono text-[9px] text-muted flex-shrink-0">{formatDateTime(entry.created_at)}</span>
                </div>
              ))}
            </div>
          </ToolRow>
        )}

        {context.acceptedShares?.length > 0 && (
          <ToolRow icon={Share2} label="Visible shared events" open={openTool === "shares"} onToggle={() => setOpenTool(openTool === "shares" ? null : "shares")}>
            <div className="flex flex-col gap-2.5">
              {context.acceptedShares.map((share) => (
                <div key={share.id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-ink truncate">{share.title}</div>
                    <div className="mono text-[10px] text-muted truncate">{share.owner_flight_name} → {share.recipient_flight_name} · {formatDateTime(share.starts_at)}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => run("hide_share", { shareId: share.id })}>Hide</Button>
                </div>
              ))}
            </div>
          </ToolRow>
        )}

      </div>
      {editUser && (
        <UserModal
          mode="admin"
          principal={principal}
          target={editUser}
          affiliations={(fullContext.staff || []).filter((s) => s.id === editUser.id)}
          squadrons={fullContext.squadrons}
          flights={fullContext.flights}
          close={() => setEditUser(null)}
          saved={reload}
          error={error}
        />
      )}
    </div>
  );
}

const STATS_RANGES = [
  ["week", "Week"],
  ["month", "Month"],
  ["year", "Year"],
  ["all", "All"]
];

const BREAKDOWN_SEGMENTS = [
  ["present_count", "Present", "oklch(0.6 0.13 150)"],
  ["absent_count", "Absent", "#c0563a"],
  ["excused_count", "Excused", "oklch(0.7 0.13 80)"],
  ["unmarked_count", "Unmarked", "#b8bcc2"]
];

const rateColor = (rate) =>
  rate >= 80 ? "oklch(0.5 0.13 150)" : rate >= 60 ? "oklch(0.55 0.14 80)" : "#c0563a";

function Analytics({ data, range, setRange }) {
  const header = (
    <div className="bg-ink -mx-4 -mt-5 px-5 pt-4 pb-4 mb-5">
      <div className="flex items-center justify-between mb-3.5">
        <h2 className="display text-[34px] leading-[0.9] text-white m-0">UNIT STATS</h2>
        <BarChart3 size={26} className="text-signal-bright" />
      </div>
      <div className="flex gap-1.5">
        {STATS_RANGES.map(([id, label]) => (
          <button
            key={id}
            onClick={() => setRange(id)}
            className={`flex-1 py-2 mono text-[10px] uppercase tracking-[0.1em] cursor-pointer border ${
              range === id
                ? "bg-signal text-ink border-signal font-semibold"
                : "bg-transparent text-muted border-ink-line font-medium"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  if (!data) {
    return (
      <div>
        {header}
        <div className="text-muted mono text-xs uppercase tracking-[0.12em]">Loading analytics...</div>
      </div>
    );
  }

  const attendance = data.attendance || {};
  const markedCount =
    Number(attendance.present_count || 0) + Number(attendance.absent_count || 0) + Number(attendance.excused_count || 0);
  const breakdownTotal = markedCount + Number(attendance.unmarked_count || 0);
  const attendanceRate = markedCount ? Math.round((Number(attendance.present_count) / markedCount) * 100) : null;
  const mock = data.mockSummary || {};
  const passRate = Number(mock.assessment_count)
    ? Math.round((Number(mock.passing_count || 0) / Number(mock.assessment_count)) * 100)
    : null;

  const trend = (data.trend || []).map((bucket) => ({
    label: bucket.label,
    rate: Number(bucket.marked_count) ? Math.round((Number(bucket.present_count) / Number(bucket.marked_count)) * 100) : 0
  }));
  const trendMax = Math.max(...trend.map((bucket) => bucket.rate), 1);
  const trendFloor = Math.max(Math.min(...trend.map((bucket) => bucket.rate), trendMax) - 8, 0);

  return (
    <div>
      {header}

      <div className="grid grid-cols-2 gap-px bg-line mb-5">
        <StatBlock
          label="Sessions"
          value={data.summary.event_count || 0}
          sub={Number(data.summary.canceled_count) ? `${data.summary.canceled_count} canceled` : null}
        />
        <StatBlock
          label="Attendance"
          value={attendanceRate === null ? "—" : <>{attendanceRate}<span className="text-lg text-muted">%</span></>}
          sub={markedCount ? `${attendance.present_count} present` : "No marks yet"}
          accent
        />
        <StatBlock
          label="Mock pass"
          value={passRate === null ? "—" : <>{passRate}<span className="text-lg text-muted">%</span></>}
          sub={`${mock.assessment_count || 0} final scores`}
        />
        <StatBlock
          label="Members"
          value={data.summary.participant_count || 0}
          sub={`${data.summary.registration_count || 0} registrations`}
        />
      </div>

      {trend.length > 1 && (
        <>
          <MonoLabel className="text-[10px] font-semibold text-ink block mb-3">Attendance trend</MonoLabel>
          <div className="bg-white px-4 pt-4 pb-3 mb-5">
            <div className="flex items-end gap-2 h-[118px]">
              {trend.map((bucket, index) => (
                <div key={`${bucket.label}-${index}`} className="flex-1 flex flex-col items-center justify-end h-full gap-1.5">
                  <div className="mono text-[10px] font-semibold text-stone-600">{bucket.rate}</div>
                  <div
                    className="w-full bg-signal min-h-[4px]"
                    style={{ height: `${((bucket.rate - trendFloor) / Math.max(trendMax - trendFloor, 1)) * 78 + 22}%` }}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2 border-t border-line-soft pt-2">
              {trend.map((bucket, index) => (
                <div key={`${bucket.label}-${index}`} className="flex-1 text-center mono text-[9px] uppercase tracking-[0.06em] text-muted">
                  {bucket.label}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <MonoLabel className="text-[10px] font-semibold text-ink block mb-3">Breakdown</MonoLabel>
      <div className="bg-white p-4 mb-5">
        {breakdownTotal ? (
          <>
            <div className="flex h-3 overflow-hidden mb-4">
              {BREAKDOWN_SEGMENTS.map(([key, , color]) => (
                <div
                  key={key}
                  style={{ width: `${(Number(attendance[key] || 0) / breakdownTotal) * 100}%`, background: color }}
                />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {BREAKDOWN_SEGMENTS.map(([key, label, color]) => (
                <div key={key}>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 inline-block" style={{ background: color }} />
                    <MonoLabel className="text-[9px]">{label}</MonoLabel>
                  </div>
                  <div className="mono text-[15px] font-semibold text-ink mt-1">
                    {attendance[key] || 0}
                    <span className="text-[11px] text-muted ml-1.5">
                      {Math.round((Number(attendance[key] || 0) / breakdownTotal) * 100)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-muted m-0">No completed sessions in this range.</p>
        )}
      </div>

      <MonoLabel className="text-[10px] font-semibold text-ink block mb-3">Mock PT test</MonoLabel>
      <div className="flex gap-px bg-line mb-5">
        <div className="flex-1 bg-white px-4 py-3.5">
          <MonoLabel className="text-[9px]">Avg composite</MonoLabel>
          <div className="display text-[38px] leading-[0.82] text-ink mt-1">{mock.average_score ?? "—"}</div>
        </div>
        <div className="flex-[1.3] bg-ink px-4 py-3.5 flex items-center gap-3">
          <Award size={26} className="text-signal-bright flex-shrink-0" />
          <div className="min-w-0">
            <MonoLabel className="text-[9px] text-stone-500 block">Top scorer</MonoLabel>
            {data.topScorer ? (
              <>
                <div className="text-[15px] font-semibold text-white mt-0.5 truncate">{data.topScorer.participant_name}</div>
                <div className="mono text-[11px] text-signal-bright">{data.topScorer.total_points} composite</div>
              </>
            ) : (
              <div className="text-sm text-muted mt-0.5">{data.identifiable ? "No final scores yet" : "Squadron admin only"}</div>
            )}
          </div>
        </div>
      </div>

      {data.members?.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3">
            <MonoLabel className="text-[10px] font-semibold text-ink">Member attendance</MonoLabel>
            <MonoLabel className="text-[9px]">By rate</MonoLabel>
          </div>
          <div className="bg-white mb-5">
            <div className="grid grid-cols-[1fr_auto_auto] gap-3.5 px-4 py-2 border-b border-line-soft">
              <MonoLabel className="text-[8px]">Member</MonoLabel>
              <MonoLabel className="text-[8px] w-14 text-right">P / Reg</MonoLabel>
              <MonoLabel className="text-[8px] w-10 text-right">Rate</MonoLabel>
            </div>
            {data.members
              .map((member) => ({
                ...member,
                rate: Number(member.marked_count) ? Math.round((Number(member.present_count) / Number(member.marked_count)) * 100) : null
              }))
              .sort((a, b) => (b.rate ?? -1) - (a.rate ?? -1))
              .map((member) => (
                <div key={`${member.participant_name}-${member.home_flight_name}`} className="grid grid-cols-[1fr_auto_auto] gap-3.5 items-center px-4 py-2.5 border-b border-line-soft last:border-b-0">
                  <span className="text-sm font-medium text-ink truncate">
                    {member.participant_name}
                    <span className="mono text-[10px] text-muted ml-2">{member.home_flight_name}</span>
                  </span>
                  <span className="mono text-xs text-stone-600 w-14 text-right">{member.present_count}/{member.registrations}</span>
                  <span className="mono text-[13px] font-semibold w-10 text-right" style={{ color: member.rate === null ? "#9ca0a6" : rateColor(member.rate) }}>
                    {member.rate === null ? "—" : `${member.rate}%`}
                  </span>
                </div>
              ))}
          </div>
        </>
      )}

      {data.fitness?.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3">
            <MonoLabel className="text-[10px] font-semibold text-ink">Activity</MonoLabel>
            <MonoLabel className="text-[9px]">Wearable totals</MonoLabel>
          </div>
          <div className="bg-white mb-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line-soft">
                  <th className="text-left px-4 py-2 mono text-[8px] uppercase tracking-[0.14em] text-muted font-normal">Member</th>
                  <th className="text-right px-4 py-2 mono text-[8px] uppercase tracking-[0.14em] text-muted font-normal">Steps</th>
                  <th className="text-right px-4 py-2 mono text-[8px] uppercase tracking-[0.14em] text-muted font-normal">Miles</th>
                  <th className="text-right px-4 py-2 mono text-[8px] uppercase tracking-[0.14em] text-muted font-normal">Cardio</th>
                </tr>
              </thead>
              <tbody>
                {data.fitness.map((m, i) => (
                  <tr key={`${m.participant_name}-${i}`} className="border-b border-line-soft last:border-b-0">
                    <td className="px-4 py-2.5 text-ink truncate">{m.participant_name}<span className="mono text-[10px] text-muted ml-2">{m.home_flight_name}</span></td>
                    <td className="px-4 py-2.5 text-right mono text-xs text-stone-600">{Number(m.steps || 0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right mono text-xs text-stone-600">{Number(m.miles || 0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right mono text-xs text-stone-600">{Number(m.cardio || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <MonoLabel className="text-[10px] font-semibold text-ink block mb-3">By flight</MonoLabel>
      <div className="bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line-soft">
              <th className="text-left px-4 py-2 mono text-[8px] uppercase tracking-[0.14em] text-muted font-normal">Flight</th>
              <th className="text-right px-4 py-2 mono text-[8px] uppercase tracking-[0.14em] text-muted font-normal">Hosted</th>
              <th className="text-right px-4 py-2 mono text-[8px] uppercase tracking-[0.14em] text-muted font-normal">Registrations</th>
              <th className="text-right px-4 py-2 mono text-[8px] uppercase tracking-[0.14em] text-muted font-normal">Guests</th>
            </tr>
          </thead>
          <tbody>
            {data.byFlight.map((flight) => (
              <tr key={flight.id} className="border-b border-line-soft last:border-b-0">
                <td className="px-4 py-2.5 text-ink">{flight.name}</td>
                <td className="px-4 py-2.5 text-right mono text-xs text-stone-600">{flight.hosted_events}</td>
                <td className="px-4 py-2.5 text-right mono text-xs text-stone-600">{flight.member_registrations}</td>
                <td className="px-4 py-2.5 text-right mono text-xs text-stone-600">{flight.hosted_guests}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AppBar({ principal, logout, onAvatar, children }) {
  const title =
    principal.kind === "staff"
      ? "PT TRACKER"
      : principal.flightName || principal.squadronName || "PT TRACKER";
  const sub =
    principal.kind === "staff"
      ? principal.name
      : principal.kind === "member"
        ? `${principal.squadronName} · Member`
        : principal.scope === "flight"
          ? `${principal.squadronName} · Flight view`
          : "Squadron · Read only";
  const displayName =
    principal.kind === "member" && principal.rank && principal.name?.startsWith(principal.rank)
      ? principal.name.slice(principal.rank.length).trim()
      : principal.name;
  const initials = initialsOf(displayName || principal.flightName || principal.squadronName);

  return (
    <header className="sticky top-0 z-20 bg-ink">
      <div className="max-w-screen-sm lg:max-w-4xl mx-auto px-5 pt-3.5 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={38} />
            <div>
              <div className="display text-[22px] leading-[0.92] tracking-[0.02em] text-white uppercase">{title}</div>
              <div className="mono text-[9px] uppercase tracking-[0.16em] text-stone-500 mt-0.5">{sub}</div>
            </div>
          </div>
          <div className="flex items-center gap-3.5">
            {onAvatar ? (
              <button onClick={onAvatar} aria-label="Your account" className="bg-transparent border-0 p-0 cursor-pointer flex">
                <Avatar initials={initials} tone="tint" />
              </button>
            ) : (
              <Avatar initials={initials} tone="tint" />
            )}
            <button
              onClick={logout}
              aria-label="Sign out"
              className="border border-ink-line text-muted hover:text-white p-2 flex cursor-pointer bg-transparent"
            >
              <LogOut size={17} />
            </button>
          </div>
        </div>
        {children}
      </div>
    </header>
  );
}

export default function MultiUnitTracker() {
  const [principal, setPrincipal] = useState(undefined);
  const [calendar, setCalendar] = useState({ events: [], flights: [] });
  const [admin, setAdmin] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [view, setView] = useState("calendar");
  const [eventFilter, setEventFilter] = useState("all");
  const [detailEventId, setDetailEventId] = useState(null);
  const [statsRange, setStatsRange] = useState("month");
  const [flightFilter, setFlightFilter] = useState("");
  const [squadronFilter, setSquadronFilter] = useState("");
  const [flightScope, setFlightScope] = useState(""); // staff: optional flight-level visibility
  const [eventModal, setEventModal] = useState(null);
  const [shareModal, setShareModal] = useState(null);
  const [mockTool, setMockTool] = useState(null);
  const [workoutTool, setWorkoutTool] = useState(null);
  const [accountModal, setAccountModal] = useState(false);
  const [error, setError] = useState("");
  const [fitnessFlash, setFitnessFlash] = useState("");
  const fitnessHandled = useRef(false);
  const [bootstrapRequired, setBootstrapRequired] = useState(false);
  const [ownedRegistrations, setOwnedRegistrations] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pt_registration_tokens_v1") || "{}"); }
    catch { return {}; }
  });
  const [redeemTokens, setRedeemTokens] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return { invite: p.get("invite"), login: p.get("login"), reset: p.get("reset") };
  });

  // All calendar events share one squadron's weather coords, so fetch the forecast
  // once and let each card read its own day/hour from it.
  const squadronWeather = useMemo(() => calendar.events.find((e) => e.weather)?.weather || null, [calendar.events]);
  const forecast = useForecast(squadronWeather);

  const refreshPrincipal = useCallback(async () => {
    const result = await apiRequest("me");
    setCsrfToken(result.csrfToken);
    setPrincipal(result.principal);
    setBootstrapRequired(Boolean(result.bootstrapRequired));
  }, []);

  const clearRedeem = useCallback(() => {
    // Drop the token from the URL so refresh / re-render doesn't re-fire it.
    window.history.replaceState(null, "", window.location.pathname);
    setRedeemTokens({ invite: null, login: null, reset: null });
  }, []);

  const handleRedeemSignIn = useCallback(async () => {
    // Load the new session BEFORE clearing the token, so the app renders straight
    // away instead of briefly falling through to the login page.
    try { await refreshPrincipal(); } catch (err) { setError(err.message); }
    clearRedeem();
  }, [clearRedeem, refreshPrincipal]);

  const loadCalendar = useCallback(async () => {
    if (!principal) return;
    const result = await apiRequest("calendar", {
      query: principal.kind === "viewer" && principal.scope === "squadron"
        ? { flightId: flightFilter }
        : principal.kind === "staff" ? { squadronId: squadronFilter } : {}
    });
    setCalendar(result);
  }, [principal, flightFilter, squadronFilter]);

  const loadAdmin = useCallback(async () => {
    if (principal?.kind !== "staff") return;
    setAdmin(await apiRequest("admin_context"));
  }, [principal]);

  const loadAnalytics = useCallback(async () => {
    if (!principal) return;
    setAnalytics(await apiRequest("analytics", {
      query: {
        range: statsRange,
        ...(principal.kind === "viewer" && principal.scope === "squadron"
          ? { flightId: flightFilter }
          : principal.kind === "staff" ? { squadronId: squadronFilter } : {})
      }
    }));
  }, [principal, flightFilter, squadronFilter, statsRange]);

  useEffect(() => {
    refreshPrincipal().catch((err) => { setError(err.message); setPrincipal(null); });
  }, [refreshPrincipal]);
  useEffect(() => { loadCalendar().catch((err) => setError(err.message)); }, [loadCalendar]);
  useEffect(() => {
    if (principal?.kind === "staff") loadAdmin().catch((err) => setError(err.message));
  }, [principal, loadAdmin]);
  useEffect(() => {
    if (!squadronFilter && admin?.squadrons?.length) {
      // Default to the admin's own associated squadron, not "all" / the first one.
      const mine = principal?.roles?.map((r) => r.squadron_id).find(Boolean);
      const known = admin.squadrons.some((s) => s.id === mine);
      setSquadronFilter(known ? mine : admin.squadrons[0].id);
    }
  }, [admin, squadronFilter, principal]);
  useEffect(() => {
    if (view === "admin") loadAdmin().catch((err) => setError(err.message));
    if (view === "analytics") loadAnalytics().catch((err) => setError(err.message));
  }, [view, loadAdmin, loadAnalytics]);

  // Returning from the Google consent screen (any logged-in user). Runs once the
  // session (and CSRF token) is loaded; syncs the freshly connected account.
  useEffect(() => {
    if (!principal || fitnessHandled.current) return;
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("fitness");
    if (!flag) return;
    fitnessHandled.current = true;
    params.delete("fitness");
    const qs = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
    if (flag === "connected") {
      apiRequest("sync_fitness", { method: "POST", body: {} })
        .then(() => setFitnessFlash("Fitness account connected and synced."))
        .catch((err) => setError(err.message));
    } else if (flag === "error") {
      setError("Couldn't connect that account. Please try again.");
    }
  }, [principal]);

  const run = async (action, body, reload = loadCalendar) => {
    try {
      await apiRequest(action, { method: "POST", body });
      await reload();
    } catch (err) {
      setError(err.message);
    }
  };

  const logout = async () => {
    await apiRequest("logout", { method: "POST" });
    setPrincipal(null);
    setCalendar({ events: [], flights: [] });
    setAdmin(null);
  };

  const saveEvent = async (form) => {
    await run("create_event", form);
    setEventModal(null);
  };

  // Mark attendance without refetching the whole calendar: each mark returns the
  // resolved attendance, which we patch into calendar state in place. One request
  // per registration (each is its own audited mark), but no full reloads — a
  // 30-person roster used to fire 30 calendar GETs. Bulk reuses the same path.
  const applyAttendance = async (registrationIds, status) => {
    if (!registrationIds.length) return;
    // null status = toggle off. unmark_attendance deletes this staffer's marks and
    // returns the now-resolved attendance (null, or the other authority's mark if any).
    const action = status ? "mark_attendance" : "unmark_attendance";
    const results = await Promise.all(registrationIds.map((registrationId) =>
      apiRequest(action, { method: "POST", body: { registrationId, status } })
        .then((r) => [registrationId, r.resolvedAttendance])
        .catch((err) => { setError(err.message); return null; })
    ));
    const patched = new Map(results.filter(Boolean));
    if (!patched.size) return;
    setCalendar((cur) => ({
      ...cur,
      events: cur.events.map((ev) => (
        ev.registrations?.some((r) => patched.has(r.id))
          ? { ...ev, registrations: ev.registrations.map((r) => patched.has(r.id) ? { ...r, attendance: patched.get(r.id) } : r) }
          : ev
      ))
    }));
  };

  const saveDetail = async (form) => {
    try {
      if (form.series && form.seriesId) {
        await apiRequest("update_series", { method: "POST", body: { ...form, scope: "future" } });
      } else {
        await apiRequest("update_event", { method: "POST", body: form });
      }
      await loadCalendar();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const deleteEvent = async (eventId) => {
    try {
      await apiRequest("delete_event", { method: "POST", body: { eventId } });
      await loadCalendar();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const deleteSeries = async (seriesId, scope = "future") => {
    try {
      await apiRequest("delete_series", { method: "POST", body: { seriesId, scope } });
      await loadCalendar();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const register = async (eventId, participantName, self = false) => {
    try {
      if (principal.kind === "member") {
        await apiRequest("member_register", { method: "POST", body: { eventId } });
        await loadCalendar();
        return;
      }
      const result = await apiRequest("register", { method: "POST", body: { eventId, participantName, ...(self ? { self: true } : {}) } });
      const next = {
        ...ownedRegistrations,
        [eventId]: [
          ...(ownedRegistrations[eventId] || []),
          { registrationId: result.registrationId, removalToken: result.removalToken, participantName }
        ]
      };
      setOwnedRegistrations(next);
      localStorage.setItem("pt_registration_tokens_v1", JSON.stringify(next));
      await loadCalendar();
    } catch (err) {
      setError(err.message);
    }
  };

  const removeRegistration = async (registration) => {
    try {
      await apiRequest("remove_registration", {
        method: "POST",
        body: {
          registrationId: registration.registrationId,
          ...(registration.removalToken ? { removalToken: registration.removalToken } : {})
        }
      });
      if (principal.kind === "member") {
        await loadCalendar();
        return;
      }
      const next = Object.fromEntries(
        Object.entries(ownedRegistrations).map(([eventId, registrations]) => [
          eventId,
          registrations.filter((item) => item.registrationId !== registration.registrationId)
        ])
      );
      setOwnedRegistrations(next);
      localStorage.setItem("pt_registration_tokens_v1", JSON.stringify(next));
      await loadCalendar();
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (flightScope && !calendar.flights.some((f) => f.id === flightScope)) setFlightScope("");
  }, [calendar.flights, flightScope]);

  const filteredEvents = useMemo(() => {
    let events = calendar.events;
    // Staff flight-level visibility scope (chosen in the profile page).
    if (flightScope && principal?.kind === "staff") {
      events = events.filter((event) => event.ownerFlightId === flightScope);
    }
    if (eventFilter === "mine") {
      return events.filter((event) =>
        principal?.kind === "member"
          ? Boolean(event.memberRegistrationId)
          : (ownedRegistrations[event.id] || []).length > 0
      );
    }
    if (eventFilter === "conditioning") return events.filter((event) => event.eventType === "PT");
    if (eventFilter === "assessment") return events.filter((event) => event.eventType === "MOCK_TEST" || event.eventType === "OFFICIAL_TEST");
    return events;
  }, [calendar.events, eventFilter, principal, ownedRegistrations, flightScope]);

  const todayCode = dateKey(new Date());

  const dayGroups = useMemo(() => {
    const byDay = new Map();
    for (const event of filteredEvents) {
      const key = dateKey(toDate(event.startsAt));
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(event);
    }
    const sortByTime = (a, b) => a.startsAt.localeCompare(b.startsAt);
    const keys = [...byDay.keys()].sort();
    return {
      upcoming: keys
        .filter((key) => key >= todayCode)
        .map((key) => ({ key, events: byDay.get(key).sort(sortByTime) })),
      past: keys
        .filter((key) => key < todayCode)
        .reverse()
        .map((key) => ({ key, events: byDay.get(key).sort(sortByTime) }))
    };
  }, [filteredEvents, todayCode]);

  const nextEventId = useMemo(() => {
    for (const group of dayGroups.upcoming) {
      const next = group.events.find((event) => event.status === "scheduled");
      if (next) return next.id;
    }
    return null;
  }, [dayGroups]);

  const eventDayKeys = useMemo(
    () => new Set(filteredEvents.map((event) => dateKey(toDate(event.startsAt)))),
    [filteredEvents]
  );

  // Today is selected by default. Tapping the selected day again clears it, which
  // switches the list to "everything in the next 10 days" instead of a single day.
  const [selectedDay, setSelectedDay] = useState(() => dateKey(new Date()));
  const onSelectDay = (key) => setSelectedDay((cur) => (cur === key ? null : key));

  const rangeEndCode = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 10);
    return dateKey(d);
  }, []);

  const visibleUpcoming = selectedDay
    ? dayGroups.upcoming.filter((group) => group.key === selectedDay)
    : dayGroups.upcoming.filter((group) => group.key < rangeEndCode);
  const visiblePast = selectedDay ? dayGroups.past.filter((group) => group.key === selectedDay) : [];

  // scroll to the selected day after its (possibly newly-shown) section renders
  useEffect(() => {
    if (selectedDay) {
      document.getElementById(`pt-day-${selectedDay}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedDay]);

  const detailEvent = detailEventId
    ? calendar.events.find((event) => event.id === detailEventId) || null
    : null;

  useEffect(() => {
    if (detailEventId && !calendar.events.some((event) => event.id === detailEventId)) {
      setDetailEventId(null);
    }
  }, [detailEventId, calendar.events]);

  // Scanned-barcode landing pages. Invite enrolment doesn't need a session (it
  // carries flight + squadron); a login pass signs an existing account in.
  if (redeemTokens.invite) {
    return <RedeemScreen inviteToken={redeemTokens.invite} onSignedIn={handleRedeemSignIn} onCancel={clearRedeem} />;
  }
  if (redeemTokens.reset) {
    return <RedeemScreen resetToken={redeemTokens.reset} onSignedIn={handleRedeemSignIn} onCancel={clearRedeem} />;
  }
  if (principal === undefined) {
    return (
      <div className="min-h-screen bg-ink text-muted mono text-xs uppercase tracking-[0.2em] flex items-center justify-center">
        Loading...
      </div>
    );
  }
  if (redeemTokens.login && !principal) {
    return <RedeemScreen loginToken={redeemTokens.login} onSignedIn={handleRedeemSignIn} onCancel={clearRedeem} />;
  }
  if (!principal) return <LoginScreen refresh={refreshPrincipal} bootstrapRequired={bootstrapRequired} />;

  const pendingCount =
    (admin?.squadronRequests?.length || 0) +
    (admin?.pendingShares?.length || 0) +
    (admin?.provisionalMembers?.length || 0) +
    (admin?.memberAccountRequests?.length || 0);

  const navItems =
    principal.kind === "staff"
      ? [
          { id: "calendar", label: "Calendar", icon: Calendar },
          { id: "analytics", label: "Stats", icon: BarChart3 },
          { id: "admin", label: "Admin", icon: Shield, badge: pendingCount }
        ]
      : principal.kind === "member"
        ? [
            { id: "calendar", label: "Calendar", icon: Calendar },
            { id: "history", label: "My Record", icon: FileText }
          ]
        : principal.scope === "flight"
          ? [
              { id: "calendar", label: "Calendar", icon: Calendar },
              { id: "analytics", label: "Stats", icon: BarChart3 },
              { id: "account", label: "Account", icon: UserPlus }
            ]
          : [
              { id: "calendar", label: "Calendar", icon: Calendar },
              { id: "analytics", label: "Stats", icon: BarChart3 }
            ];

  if (detailEvent) {
    return (
      <div className="min-h-screen bg-surface flex flex-col" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
        <Notice error={error} clear={() => setError("")} />
        <EventDetail
          event={detailEvent}
          principal={principal}
          ownedRegistrations={ownedRegistrations}
          onBack={() => setDetailEventId(null)}
          onRegister={register}
          onRemove={removeRegistration}
          onAttendance={(registrationId, status) => applyAttendance([registrationId], status)}
          onBulkAttendance={applyAttendance}
          onSave={saveDetail}
          onDeleteEvent={deleteEvent}
          onDeleteSeries={deleteSeries}
          onShare={setShareModal}
          onPush={(eventId) => run("push_event", { eventId })}
          onMockTool={setMockTool}
          onWorkout={setWorkoutTool}
          onRosterAdded={loadCalendar}
        />
        {shareModal && (
          <ShareModal
            event={shareModal}
            flights={admin?.flights || calendar.flights}
            close={() => setShareModal(null)}
            share={async (eventId, recipientFlightId) => {
              await run("share_event", { eventId, recipientFlightId });
              setShareModal(null);
            }}
          />
        )}
        {mockTool && (
          <MockTestTools
            {...mockTool}
            close={() => setMockTool(null)}
            changed={loadCalendar}
          />
        )}
        {workoutTool && (
          <WorkoutPlayer
            {...workoutTool}
            close={() => setWorkoutTool(null)}
            changed={loadCalendar}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex flex-col" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <Notice error={error} clear={() => setError("")} />
      {fitnessFlash && (
        <div className="bg-signal text-ink px-4 py-2 mono text-[11px] flex justify-between items-center">
          <span>{fitnessFlash}</span>
          <button type="button" onClick={() => setFitnessFlash("")} className="bg-transparent border-0 text-ink cursor-pointer p-0" aria-label="Dismiss">✕</button>
        </div>
      )}
      <AppBar principal={principal} logout={logout} onAvatar={principal.kind === "staff" ? () => setAccountModal(true) : undefined}>
        {principal.kind === "viewer" && principal.scope === "squadron" && (
          <div className="mt-3 flex items-center gap-3">
            <label className="mono text-[9px] uppercase tracking-[0.16em] text-stone-500 flex-shrink-0">Flight</label>
            <select className={appBarSelectClass} value={flightFilter} onChange={(e) => setFlightFilter(e.target.value)}>
              <option value="">All squadron events</option>
              {calendar.flights.map((flight) => <option key={flight.id} value={flight.id}>{flight.name}</option>)}
            </select>
          </div>
        )}
        {principal.kind === "staff" && admin?.squadrons?.length > 1 && (
          <div className="mt-3 flex items-center gap-3">
            <label className="mono text-[9px] uppercase tracking-[0.16em] text-stone-500 flex-shrink-0">{view === "admin" ? "Administer" : "Squadron"}</label>
            <select className={appBarSelectClass} value={squadronFilter} onChange={(e) => setSquadronFilter(e.target.value)}>
              {admin.squadrons.map((squadron) => <option key={squadron.id} value={squadron.id}>{squadron.name}</option>)}
            </select>
          </div>
        )}
        {view === "calendar" && <WeekStrip eventDayKeys={eventDayKeys} selectedDay={selectedDay} onSelectDay={onSelectDay} />}
      </AppBar>

      <main className="flex-1">
        <div className="max-w-screen-sm lg:max-w-4xl mx-auto px-4 pt-5 pb-28">
        {view === "calendar" && (
          <>
            <div className="flex justify-between items-end mb-4">
              <h2 className="display text-[44px] leading-[0.85] text-ink m-0">EVENTS</h2>
              <MonoLabel>{dayGroups.upcoming.reduce((n, group) => n + group.events.length, 0)} upcoming</MonoLabel>
            </div>
            <div className="flex gap-2 overflow-x-auto mb-3 -mx-4 px-4">
              {[
                ["all", "All"],
                ...(principal.kind === "member" || (principal.kind === "viewer" && principal.scope === "flight")
                  ? [["mine", "Mine"]]
                  : []),
                ["conditioning", "Conditioning"],
                ["assessment", "Assessment"]
              ].map(([id, label]) => (
                <Chip key={id} active={eventFilter === id} onClick={() => setEventFilter(id)}>{label}</Chip>
              ))}
            </div>

            {!selectedDay && (
              <SectionHeader className="mt-2.5 mb-3">Next 10 days</SectionHeader>
            )}

            {visibleUpcoming.map((group) => (
              <section key={group.key} id={`pt-day-${group.key}`} className="scroll-mt-44">
                <SectionHeader className="mt-2.5 mb-3">
                  {group.key === todayCode ? `Today · ${dayLabel(group.key)}` : dayLabel(group.key)}
                </SectionHeader>
                {group.events.map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    principal={principal}
                    ownedRegistrations={ownedRegistrations}
                    forecast={forecast}
                    tone={event.id === nextEventId ? "signal" : "ink"}
                    onOpen={() => setDetailEventId(event.id)}
                    onRegister={register}
                    onRemove={removeRegistration}
                  />
                ))}
              </section>
            ))}

            {visiblePast.map((group) => (
              <section key={group.key} id={`pt-day-${group.key}`} className="scroll-mt-44">
                <SectionHeader muted className="mt-5 mb-3">{`Completed · ${dayLabel(group.key)}`}</SectionHeader>
                {group.events.map((event) => (
                  <PastEventCard
                    key={event.id}
                    event={event}
                    attended={Boolean(event.memberRegistrationId) || (ownedRegistrations[event.id] || []).length > 0}
                    onOpen={() => setDetailEventId(event.id)}
                  />
                ))}
              </section>
            ))}

            {visibleUpcoming.length === 0 && visiblePast.length === 0 && (
              <div className="border border-dashed border-line p-10 text-center text-muted mono text-xs uppercase tracking-[0.12em]">
                {selectedDay ? `No sessions on ${dayLabel(selectedDay)}.` : "No sessions in the next 10 days."}
              </div>
            )}
          </>
        )}
        {view === "analytics" && <Analytics data={analytics} range={statsRange} setRange={setStatsRange} />}
        {view === "admin" && principal.kind === "staff" && (
          admin
            ? <AdminPanel principal={principal} context={admin} scopeId={squadronFilter} flightScope={flightScope} reload={loadAdmin} error={setError} />
            : <div className="text-muted mono text-xs uppercase tracking-[0.12em]">Loading administration...</div>
        )}
        {view === "history" && principal.kind === "member" && <MemberHistory principal={principal} changed={refreshPrincipal} />}
        {view === "account" && principal.kind === "viewer" && principal.scope === "flight" && <MemberAccountRequest error={setError} />}
        </div>
      </main>

      {principal.kind === "staff" && view === "calendar" && (
        <button
          onClick={() => setEventModal({ mode: "create" })}
          aria-label="Create event"
          className="fixed right-5 bottom-[88px] z-20 w-14 h-14 bg-signal text-ink border-0 cursor-pointer flex items-center justify-center shadow-[0_14px_28px_-10px_rgba(0,0,0,0.45)]"
        >
          <Plus size={24} />
        </button>
      )}

      <BottomNav items={navItems} active={view} onSelect={setView} />

      {eventModal && (
        <EventModal
          flights={admin?.flights || calendar.flights}
          principal={principal}
          close={() => setEventModal(null)}
          save={saveEvent}
        />
      )}
      {principal.kind === "member" && principal.mustUpdateCredentials && (
        <MemberCredentialModal principal={principal} onSaved={refreshPrincipal} />
      )}
      {(accountModal || principal.mustResetPassword) && principal.kind === "staff" && (
        <UserModal
          mode="self"
          forced={Boolean(principal.mustResetPassword)}
          principal={principal}
          affiliations={principal.roles}
          scopeSquadrons={admin?.squadrons || []}
          scopeFlights={calendar.flights || []}
          scopeSquadron={squadronFilter}
          onScopeSquadron={(id) => { setSquadronFilter(id); setFlightScope(""); }}
          flightScope={flightScope}
          onFlightScope={setFlightScope}
          close={() => setAccountModal(false)}
          saved={refreshPrincipal}
          error={setError}
        />
      )}
      {shareModal && (
        <ShareModal
          event={shareModal}
          flights={admin?.flights || calendar.flights}
          close={() => setShareModal(null)}
          share={async (eventId, recipientFlightId) => {
            await run("share_event", { eventId, recipientFlightId });
            setShareModal(null);
          }}
        />
      )}
      {mockTool && (
        <MockTestTools
          {...mockTool}
          close={() => setMockTool(null)}
          changed={loadCalendar}
        />
      )}
    </div>
  );
}
